/**
 * Raketech Final Report v4
 *
 * v3 took Adwin's invoicing from `/api/raketech/compare`, which matched the
 * invoice detail against the accounts in Raketech's June 2025 file only.
 * Accounts that first appear in their July 2026 report — everything created
 * from December 2024 — never received their lines and read as invoiced at nil,
 * which is how 48 accounts came to be marked "never reported to Adwin" when
 * Adwin had invoiced them on Raketech's own monthly reports. Raketech raised
 * this on 23 September 2026 with Paratiisi (18466).
 *
 * v4 takes the invoicing from `/api/raketech/invoice-attribution` instead:
 * every line placed once, on one account, across every account either side
 * has named, by the deal code on the line first and by brand only where no
 * code identifies it. The attribution carries its own accounting — every euro
 * of the detail is either on an account or listed as unplaced — and the
 * drilldown says which rule placed each deal group. Everything else is v3's.
 *
 * v1, v2 and v3 are untouched at their own routes.
 */

import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  AlertTriangle,
  ArrowDownRight,
  Calendar,
  Database,
  Download,
  FileText,
  Loader2,
  RefreshCw,
  Scale,
  Shield,
  Sigma,
  ChevronDown,
  ChevronRight,
  Share2,
  Link as LinkIcon,
  Lock,
  Check,
  Trash2,
  X,
} from 'lucide-react';
import { toast } from 'sonner';

import {
  allocateNeverBilled,
  buildBillingTieOut,
  buildClaim,
  buildClaimBridge,
  buildOutstanding,
  marginWaivedSince,
  RECON_END,
  SETTLEMENT_FACTS,
  type CompareResponse,
  type DealBasisDataset,
} from '../lib/raketechFinalReportV4';
import type { AttributionResponse } from '@shared/raketech/invoiceAttribution';
import type { AccessStatusResponse } from '@shared/raketech/accessStatus.types';
import { AccessTab, GapsTab, PaNoTab, UnverifiableTab, WriteDownsTab, type ShareRequest } from '../components/raketech/FinalReportV4Tabs';
import { isDefaultWriteDown, isPaNoWithPlayers } from '../lib/raketechFocus';
import { accessByAccount, noAccess, verifyReasons } from '../lib/raketechAccess';
import {
  CLAIM_SOURCE_LABELS,
  type ClaimBridgeStep,
  type ClaimRow,
  type ClaimSource,
  type OutstandingLine,
} from '../types/raketechFinalReportV4.types';
import { buildSharePayload } from '../lib/raketechShareV3';
import type { NewReconDataset } from '../types/raketechReconDiff.types';
import type { JuneReconDataset } from '../types/raketechReconJune.types';

interface SnapshotMeta {
  id: string;
  slug: string;
  title: string;
  lastMonth: string;
  createdAt: string;
  viewCount: number;
  hasPassword: boolean;
  url: string;
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------
const getAuthToken = (): string | null => {
  try {
    const auth = localStorage.getItem('adwstats-auth');
    if (auth) return JSON.parse(auth).state?.token || null;
  } catch { /* ignore */ }
  return null;
};

/**
 * v2 reaches Dec 2024 onward from Raketech's own columns, so it does not need an
 * Affiliation Cloud session the way v1 does. If the user happens to already hold
 * a valid token from another page we use it for a free cross-check, but nothing
 * here prompts for one.
 */
const getExistingACToken = (): string | null => {
  try {
    const stored = localStorage.getItem('affiliation-cloud-token');
    if (stored) {
      const parsed = JSON.parse(stored);
      if (parsed.expiresAt && Date.now() < parsed.expiresAt) return parsed.token;
    }
  } catch { /* ignore */ }
  return null;
};

interface PostReconResponse {
  rows?: {
    reconAccount?: { accountId?: string };
    acReportCommission?: number;
    acMonthly?: { month: string; commission: number }[];
  }[];
}

const fmtInt = (v: number) => v.toLocaleString('en-US');

const fmtEUR = (v: number) =>
  new Intl.NumberFormat('en-US', {
    style: 'currency', currency: 'EUR', minimumFractionDigits: 0, maximumFractionDigits: 0,
  }).format(v);

const fmtEURExact = (v: number) =>
  new Intl.NumberFormat('en-US', {
    style: 'currency', currency: 'EUR', minimumFractionDigits: 2, maximumFractionDigits: 2,
  }).format(v);

const MONTH_SHORT = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
const fmtMonth = (m: string) => {
  if (!/^\d{4}-\d{2}$/.test(m)) return m;
  const [y, mo] = m.split('-');
  return `${MONTH_SHORT[parseInt(mo, 10) - 1] || mo} ${y}`;
};

const SOURCE_STYLES: Record<ClaimSource, string> = {
  june_recon: 'bg-sky-500/10 text-sky-300 border-sky-500/20',
  june_reported: 'bg-violet-500/10 text-violet-300 border-violet-500/20',
  synced: 'bg-emerald-500/10 text-emerald-400 border-emerald-500/20',
  manual_check: 'bg-cyan-500/10 text-cyan-400 border-cyan-500/20',
  raketech_restated: 'bg-amber-500/10 text-amber-400 border-amber-500/20',
};

const SOURCE_SHORT: Record<ClaimSource, string> = {
  june_recon: 'June recon',
  june_reported: 'Their reports',
  synced: 'Synced',
  manual_check: 'Manual',
  raketech_restated: 'Their restatement',
};

type MainTab = 'underpaid' | 'overpaid' | 'all' | 'outstanding' | 'coverage' | 'bridge' | 'sources' | 'june' | 'unverifiable' | 'access' | 'pano' | 'writedowns';

/** The three tabs that share the account table, and how each selects rows. */
const ROW_TABS = {
  underpaid: {
    label: 'Underpaid',
    /** Negative means they invoiced less than the account earned. */
    match: (r: ClaimRow) => r.underpaid < -1,
    sort: (a: ClaimRow, b: ClaimRow) => a.underpaid - b.underpaid,
    tone: 'red' as const,
  },
  overpaid: {
    label: 'Overpaid',
    match: (r: ClaimRow) => r.underpaid > 1,
    sort: (a: ClaimRow, b: ClaimRow) => b.underpaid - a.underpaid,
    tone: 'amber' as const,
  },
  all: {
    label: 'All accounts',
    match: () => true,
    sort: (a: ClaimRow, b: ClaimRow) => Math.abs(b.underpaid) - Math.abs(a.underpaid),
    tone: 'zinc' as const,
  },
};

const isRowTab = (t: MainTab): t is keyof typeof ROW_TABS => t in ROW_TABS;

// ---------------------------------------------------------------------------
// Page
// ---------------------------------------------------------------------------
/** Plain names for the attribution rules, for the drilldown and the panel. */
const RULE_LABELS: Record<string, string> = {
  code: 'deal code',
  code_token: 'deal code inside a longer label',
  code_stem: 'deal code without its suffix',
  brand_id_code: 'deal code via brand id',
  brand: 'brand',
  brand_in_label: 'brand named in the label',
  none: 'nothing',
};

/**
 * How the invoice detail was placed. The identity at the top is the check:
 * every euro of the detail is on an account or listed below as unplaced.
 */
const AttributionPanel: React.FC<{ data: AttributionResponse; onClose: () => void }> = ({ data, onClose }) => (
  <div className="mb-6 rounded-xl border border-[hsl(225,15%,18%)] bg-[hsl(225,15%,9%)] p-5">
    <div className="flex items-start justify-between gap-4 mb-3">
      <div>
        <h2 className="text-sm font-semibold text-white">How Adwin's invoice detail was placed</h2>
        <p className="text-xs text-zinc-500 mt-1 max-w-3xl leading-relaxed">
          {fmtInt(data.lineCount)} lines of Adwin's invoice detail, {fmtEURExact(data.conservation.all)} in all.
          Each is placed on one Raketech account or on none, by the strongest rule that identifies it.
          {' '}{fmtEURExact(data.conservation.attributed)} is on accounts and {fmtEURExact(data.conservation.unattributed)} is
          placed on none; the two {data.conservation.balanced ? 'add back to the total' : 'DO NOT add back to the total'}.
          Where a deal code sits on more than one account, the line's own brand, partner and geo decide, then
          which account Raketech itself reports under that code in that month. Where several siblings are still
          reported for the month, the line is apportioned in the proportions Raketech's own report gives them
          ({fmtInt(data.split?.lines ?? 0)} lines, {fmtEUR(data.split?.revenue ?? 0)}) — Adwin invoiced on that report,
          so its split is the invoice's split. Only when Raketech reports on none of them does the larger account
          take it, and those lines are listed here so the judgement is visible.
        </p>
      </div>
      <button onClick={onClose} className="text-zinc-500 hover:text-zinc-300"><X className="h-4 w-4" /></button>
    </div>
    <div className="grid grid-cols-2 md:grid-cols-4 gap-2 mb-4 text-xs">
      {Object.entries(data.ruleCounts).filter(([, c]) => c.lines > 0).map(([rule, c]) => (
        <div key={rule} className="rounded-lg border border-[hsl(225,15%,16%)] px-3 py-2">
          <div className="text-zinc-500 text-[11px]">{RULE_LABELS[rule] || rule}</div>
          <div className="text-zinc-200 tabular-nums">{fmtInt(c.lines)} lines · {fmtEUR(c.revenue)}</div>
        </div>
      ))}
    </div>
    {data.unattributed.length > 0 && (
      <div className="mb-4">
        <p className="text-[11px] text-zinc-500 mb-1">Placed on no account — no Raketech source names the brand or the code</p>
        <table className="w-full text-[11px]">
          <tbody className="divide-y divide-[hsl(225,15%,13%)]">
            {data.unattributed.map((l) => (
              <tr key={l.id} className="text-zinc-400">
                <td className="py-1 pr-2">{fmtMonth(l.month)}</td>
                <td className="py-1 pr-2 text-amber-400/80 break-all">{l.offer || '—'}</td>
                <td className="py-1 pr-2">{l.brand}</td>
                <td className="py-1 pr-2 text-zinc-500">{l.partner}</td>
                <td className="py-1 pr-2 text-zinc-500">{l.geo}</td>
                <td className="py-1 text-right tabular-nums">{fmtEURExact(l.total)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    )}
    {data.judged.length > 0 && (
      <div>
        <p className="text-[11px] text-zinc-500 mb-1">
          Placed on account size alone — the code sits on every account named and nothing on the line or in Raketech's reports separates them
        </p>
        <table className="w-full text-[11px]">
          <tbody className="divide-y divide-[hsl(225,15%,13%)]">
            {data.judged.map((l) => (
              <tr key={l.id} className="text-zinc-400">
                <td className="py-1 pr-2">{fmtMonth(l.month)}</td>
                <td className="py-1 pr-2 text-amber-400/80 break-all">{l.offer || '—'}</td>
                <td className="py-1 pr-2">{l.brand}</td>
                <td className="py-1 pr-2 text-zinc-500">{(l.candidates || []).join(' / ')} → <span className="text-zinc-300">{l.accountId}</span></td>
                <td className="py-1 text-right tabular-nums">{fmtEURExact(l.total)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    )}
    {(data.duplicates?.length ?? 0) > 0 && (
      <div className="mt-4">
        <p className="text-[11px] text-zinc-500 mb-1">
          Exact repeats of another line in the same month ({data.duplicates.length} lines, {fmtEURExact(data.duplicates.reduce((s, l) => s + l.total, 0))}).
          Kept in: if Adwin invoiced the line twice it was invoiced twice, and leaving it in is the reading against Adwin.
          Each has been checked against Raketech's July report, which carries one copy's worth; they are listed so the invoice PDFs can settle it.
        </p>
        <table className="w-full text-[11px]">
          <tbody className="divide-y divide-[hsl(225,15%,13%)]">
            {data.duplicates.map((l) => (
              <tr key={l.id} className="text-zinc-400">
                <td className="py-1 pr-2">{fmtMonth(l.month)}</td>
                <td className="py-1 pr-2 text-amber-400/80 break-all">{l.offer || '—'}</td>
                <td className="py-1 pr-2">{l.brand}</td>
                <td className="py-1 pr-2 text-zinc-500">{l.partner} {l.geo}</td>
                <td className="py-1 text-right tabular-nums">{fmtEURExact(l.total)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    )}
  </div>
);

const RaketechFinalReportV4: React.FC = () => {
  const [dataset, setDataset] = useState<NewReconDataset | null>(null);
  const [june, setJune] = useState<JuneReconDataset | null>(null);
  const [compare, setCompare] = useState<CompareResponse | null>(null);
  const [deals, setDeals] = useState<DealBasisDataset | null>(null);
  const [attribution, setAttribution] = useState<AttributionResponse | null>(null);
  const [access, setAccess] = useState<AccessStatusResponse | null>(null);
  const [showAttribution, setShowAttribution] = useState(false);
  const [postRecon, setPostRecon] = useState<PostReconResponse | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const [applyMargin, setApplyMargin] = useState(true);
  const [lastMonth, setLastMonth] = useState('2026-05');
  const [activeTab, setActiveTab] = useState<MainTab>('underpaid');
  const [searchTerm, setSearchTerm] = useState('');
  const [expanded, setExpanded] = useState<Set<string>>(new Set());
  const [onlyRestatement, setOnlyRestatement] = useState(false);
  const [creditedFeeLines, setCreditedFeeLines] = useState<'part_payment' | 'flat_fee'>('part_payment');
  // Defaults match what the All accounts tab shows and what the shared snapshot
  // and the enclosure are built from: each account on its own best figure, with
  // shortfalls and overpayments stated separately rather than netted. Anything
  // sent alongside that report has to tie to it.
  const [claimBasis, setClaimBasis] = useState<'stated' | 'monthLevel' | 'likeForLike'>('stated');
  const [claimScope, setClaimScope] = useState<
    'net' | 'netEvidenced' | 'byPartner' | 'byPartnerEvidenced' | 'byAccount'
  >('byAccount');

  const [showShare, setShowShare] = useState(false);
  // A tab can ask to share only its selection; null means the whole position.
  const [shareSelection, setShareSelection] = useState<ShareRequest | null>(null);
  const [shareTitle, setShareTitle] = useState('Adwin — working reconciliation position');
  const [shareDesc, setShareDesc] = useState('');
  const [sharePassword, setSharePassword] = useState('');
  const [isSaving, setIsSaving] = useState(false);
  const [savedUrl, setSavedUrl] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);
  const [snapshots, setSnapshots] = useState<SnapshotMeta[]>([]);

  const load = useCallback(async () => {
    const token = getAuthToken();
    if (!token) { setError('Not authenticated'); return; }
    setIsLoading(true);
    setError(null);
    try {
      const headers = { Authorization: `Bearer ${token}` };
      const [dsResp, junResp, cmpResp, dealResp, attrResp] = await Promise.all([
        fetch('/api/raketech/recon-new', { headers }),
        fetch('/api/raketech/recon-june', { headers }),
        fetch('/api/raketech/compare?invoicePeriod=all', { headers }),
        fetch('/api/raketech/deal-basis', { headers }),
        fetch('/api/raketech/invoice-attribution', { headers }),
      ]);
      // The access list only annotates rows, so a failure to load it must not
      // stop the report.
      fetch('/api/raketech/access-status', { headers })
        .then((r) => r.json() as Promise<{ success: boolean; data?: AccessStatusResponse }>)
        .then((j) => setAccess(j.success && j.data ? j.data : null))
        .catch(() => setAccess(null));
      const attrJson = await attrResp.json() as { success: boolean; data?: AttributionResponse; error?: string };
      if (!attrJson.success || !attrJson.data) throw new Error(attrJson.error || 'Failed to place the invoice detail against accounts');
      const dsJson = await dsResp.json() as { success: boolean; data?: NewReconDataset; error?: string };
      const junJson = await junResp.json() as { success: boolean; data?: JuneReconDataset; error?: string };
      const cmpJson = await cmpResp.json() as { success: boolean; data?: CompareResponse & { empty?: boolean; message?: string }; error?: string };

      if (!dsJson.success || !dsJson.data) throw new Error(dsJson.error || 'Failed to load the new reconciliation');
      if (!junJson.success || !junJson.data) throw new Error(junJson.error || 'Failed to load the June 2025 reconciliation');
      if (!cmpJson.success || !cmpJson.data) throw new Error(cmpJson.error || 'Failed to load the comparison');
      if (cmpJson.data.empty) throw new Error(cmpJson.data.message || 'No reconciliation data imported yet');

      setDataset(dsJson.data);
      setJune(junJson.data);
      setCompare(cmpJson.data);
      setAttribution(attrJson.data);
      // The deal terms only restrict how accounts may be valued, so a failure
      // to load them must not stop the report; it falls back to unknown.
      const dealJson = await dealResp.json().catch(() => null) as
        { success: boolean; data?: DealBasisDataset } | null;
      setDeals(dealJson?.success ? dealJson.data || null : null);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load');
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  // Kept separate from `load` so changing the month does not re-run the
  // expensive comparison query.
  const loadAcCrossCheck = useCallback(async () => {
    const token = getAuthToken();
    const acToken = getExistingACToken();
    if (!token || !acToken) { setPostRecon(null); return; }
    try {
      const resp = await fetch(`/api/raketech/post-recon?lastMonth=${lastMonth}`, {
        headers: { Authorization: `Bearer ${token}`, 'X-Affiliation-Cloud-Token': acToken },
      });
      const json = await resp.json() as { success: boolean; data?: PostReconResponse };
      setPostRecon(json.success && json.data ? json.data : null);
    } catch {
      setPostRecon(null);
    }
  }, [lastMonth]);

  useEffect(() => { loadAcCrossCheck(); }, [loadAcCrossCheck]);

  const acMonthlyByAccountId = useMemo(() => {
    const map = new Map<string, Map<string, number>>();
    for (const row of postRecon?.rows || []) {
      const id = String(row.reconAccount?.accountId || '').trim();
      if (!id) continue;
      const months = map.get(id) || new Map<string, number>();
      for (const m of row.acMonthly || []) {
        months.set(m.month, (months.get(m.month) || 0) + (m.commission || 0));
      }
      map.set(id, months);
    }
    return map;
  }, [postRecon]);

  const claim = useMemo(() => {
    if (!dataset || !june || !compare || !attribution) return null;
    return buildClaim(compare, dataset, june, {
      attribution,
      lastMonth,
      applyMargin,
      acMonthlyByAccountId: acMonthlyByAccountId.size > 0 ? acMonthlyByAccountId : undefined,
      deals: deals || undefined,
    });
  }, [dataset, june, compare, attribution, lastMonth, applyMargin, acMonthlyByAccountId, deals]);

  // Raketech's June file records every username an account has sat under;
  // the no-login list is keyed on those, so the join needs all of them.
  const usernamesById = useMemo(() => {
    const m = new Map<string, string[]>();
    for (const a of june?.accounts || []) m.set(String(a.id), a.usernames || []);
    return m;
  }, [june]);
  const accessMap = useMemo(() => accessByAccount(claim?.rows || [], access, usernamesById), [claim, access, usernamesById]);
  const evidenceCounts = useMemo(() => {
    const rows = claim?.rows || [];
    let unverifiable = 0, noLogin = 0, paNo = 0, writeDowns = 0;
    for (const r of rows) {
      const info = accessMap.get(r.accountId);
      const reasons = verifyReasons(r, info);
      if (reasons.length > 0 && (r.earnedTotal > 0 || r.invoicedTotal > 0)) unverifiable += 1;
      if (noAccess(info)) noLogin += 1;
      // Both badges count what the tab shows on its default filters.
      if (isPaNoWithPlayers(r)) paNo += 1;
      if (isDefaultWriteDown(r)) writeDowns += 1;
    }
    return { unverifiable, noLogin, paNo, writeDowns };
  }, [claim, accessMap]);

  const tieOut = useMemo(() => {
    if (!dataset || !claim) return null;
    return buildBillingTieOut(dataset, claim.rows, lastMonth);
  }, [dataset, claim, lastMonth]);

  const neverBilledSplit = useMemo(() => {
    if (!tieOut || !claim) return null;
    return allocateNeverBilled(tieOut, claim.rows);
  }, [tieOut, claim]);

  const shareFromTab = useCallback((req: ShareRequest) => {
    setShareSelection(req);
    setShareTitle(req.focus.title);
    // The shared page states the selection itself; the description is for
    // anything the sender wants to add.
    setShareDesc('');
    setSavedUrl(null);
    setShowShare(true);
  }, []);

  const bridge = useMemo<ClaimBridgeStep[]>(() => {
    if (!dataset || !june || !claim) return [];
    return buildClaimBridge(dataset, june, claim.summary);
  }, [dataset, june, claim]);

  const outstanding = useMemo(() => {
    if (!claim) return null;
    const band = claim.summary.band;
    // The tie-out has to follow the basis: like for like deducts only the
    // invoicing that falls in a month their figures describe, so that is the
    // figure to measure against what Adwin actually billed.
    const deducted = claimBasis === 'likeForLike'
      ? claim.summary.describedInvoicedTotal
      : claim.summary.invoicedTotal;
    return buildOutstanding({
      commissionShortfall: band[claimBasis][claimScope],
      marginWaived: marginWaivedSince(claim.rows, lastMonth),
      creditedFeeLines,
      claimInvoiced: deducted,
      invoicedThrough: claim.summary.invoiceDataThrough,
      earningsBeyondInvoiceData: claim.summary.earningsBeyondInvoiceData,
      // On the account-by-account bases the tie-out on Raketech's own ledger is
      // the figure; like for like deducts less, so its own difference stands.
      neverBilled: claimBasis === 'likeForLike' ? undefined : tieOut?.neverBilled,
      // Netted scopes already carry both directions, so the never-billed head
      // adds without overlap. Shortfalls-only scopes set the overpaid accounts
      // aside, so the part of the head falling on those accounts comes out.
      scopeNets: claimScope === 'net' || claimScope === 'netEvidenced',
      neverBilledOverlap: neverBilledSplit?.overlapWithOverpaid,
      neverBilledOverlapAccounts: neverBilledSplit?.overlapAccounts.length,
    });
  }, [claim, creditedFeeLines, claimBasis, claimScope, tieOut, neverBilledSplit]);

  /** Counts for the tab labels, before search narrows anything down. */
  const tabCounts = useMemo(() => {
    const counts = { underpaid: 0, overpaid: 0, all: 0 };
    for (const r of claim?.rows || []) {
      counts.all += 1;
      if (ROW_TABS.underpaid.match(r)) counts.underpaid += 1;
      if (ROW_TABS.overpaid.match(r)) counts.overpaid += 1;
    }
    return counts;
  }, [claim]);

  const rowTab = isRowTab(activeTab) ? activeTab : 'underpaid';

  const claimRows = useMemo(() => {
    if (!claim) return [];
    const spec = ROW_TABS[rowTab];
    let rows = claim.rows.filter(spec.match);
    if (onlyRestatement) rows = rows.filter((r) => r.attributableToRestatement > 1);
    if (searchTerm) {
      const q = searchTerm.toLowerCase();
      rows = rows.filter((r) =>
        r.partner.toLowerCase().includes(q)
        || r.username.toLowerCase().includes(q)
        || r.accountId.includes(q)
        || r.acIds.some((id) => id.includes(q))
        || r.brands.some((b) => b.toLowerCase().includes(q)));
    }
    return [...rows].sort(spec.sort);
  }, [claim, rowTab, onlyRestatement, searchTerm]);

  const claimTotals = useMemo(() => {
    let invoiced = 0, earned = 0, underpaid = 0, restatement = 0;
    for (const r of claimRows) {
      invoiced += r.invoicedTotal;
      earned += r.earnedTotal;
      underpaid += r.underpaid;
      restatement += r.attributableToRestatement;
    }
    return { invoiced, earned, underpaid, restatement, count: claimRows.length };
  }, [claimRows]);

  const toggleRow = (key: string) => {
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key); else next.add(key);
      return next;
    });
  };

  /**
   * The whole comparison, frozen and given a link. What is sent to Raketech has
   * to keep reading the same months later, so the rows are stored as computed
   * rather than rebuilt by the viewer from data that will have moved on.
   */
  const fetchSnapshots = useCallback(async () => {
    const token = getAuthToken();
    if (!token) return;
    try {
      const resp = await fetch('/api/raketech/report-snapshots', { headers: { Authorization: `Bearer ${token}` } });
      const json = await resp.json() as { success: boolean; snapshots?: SnapshotMeta[] };
      if (json.success) setSnapshots(json.snapshots || []);
    } catch { /* the list is a convenience; failing to load it is not an error */ }
  }, []);

  useEffect(() => { fetchSnapshots(); }, [fetchSnapshots]);

  const saveSnapshot = async () => {
    const token = getAuthToken();
    if (!token || !claim) return;
    setIsSaving(true);
    try {
      const payload = buildSharePayload(shareSelection?.rows ?? claim.rows, claim.summary, {
        applyMargin, lastMonth, version: 4, focus: shareSelection?.focus, flags: shareSelection?.flags,
      });
      const resp = await fetch('/api/raketech/report-snapshots', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({
          title: shareTitle,
          description: shareDesc || undefined,
          password: sharePassword || undefined,
          lastMonth,
          data: payload,
        }),
      });
      const json = await resp.json() as { success: boolean; snapshot?: { url: string }; error?: string };
      if (!json.success || !json.snapshot) throw new Error(json.error || 'Failed to save');
      setSavedUrl(`${window.location.origin}${json.snapshot.url}`);
      toast.success('Snapshot saved');
      fetchSnapshots();
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Failed to save');
    } finally {
      setIsSaving(false);
    }
  };

  const deleteSnapshot = async (id: string) => {
    const token = getAuthToken();
    if (!token) return;
    try {
      await fetch(`/api/raketech/report-snapshots?id=${id}`, {
        method: 'DELETE', headers: { Authorization: `Bearer ${token}` },
      });
      toast.success('Snapshot deleted');
      fetchSnapshots();
    } catch { toast.error('Could not delete it'); }
  };

  const closeShare = () => {
    setShowShare(false);
    setSavedUrl(null);
    setSharePassword('');
  };

  const exportCsv = () => {
    if (claimRows.length === 0) { toast.error('Nothing to export'); return; }
    const header = [
      'Account ID', 'AC IDs', 'Partner', 'Username', 'Brands', 'Geo', 'Evidence source',
      'Invoiced Mar23-Nov24', 'Invoiced Dec24+', 'Invoiced total',
      // Every candidate, so the export shows what was rejected as well as chosen.
      'June recon', 'Synced -5%', 'Manual -5%', 'Their restated',
      'Earned Mar23-Nov24', 'Earned Dec24+', 'AC Dec24+ (reference)', 'Earned total',
      'Difference', 'Difference %', 'Of which from their restatement',
      // Reference only: never claimed on, carried so the export can be audited.
      'January file (not claimed)', 'Removed with January column',
      'Their reported (EUR)', 'Their reported less credited', 'Their comment',
      'Their July Adwin earnings column', 'July report below their monthly report Dec24+',
      'New to this report', 'Verifiable in adwstats',
    ];
    const candidate = (r: ClaimRow, source: ClaimSource) => {
      const c = r.candidates.find((x) => x.source === source);
      return c && c.available ? c.amount : '';
    };
    const lines = claimRows.map((r) => [
      r.accountId, r.acIds.join(' '), r.partner, r.username, r.brands.join(' / '), r.geos.join(' '),
      CLAIM_SOURCE_LABELS[r.chosenSource],
      r.invoicedRecon, r.invoicedPostRecon, r.invoicedTotal,
      candidate(r, 'june_recon'), candidate(r, 'synced'),
      candidate(r, 'manual_check'), candidate(r, 'raketech_restated'),
      r.earnedReconPeriod, r.earnedTail, r.earnedTailFromAc, r.earnedTotal,
      r.underpaid, r.underpaidPct, r.attributableToRestatement,
      r.reference.previouslyClaimed, r.januaryColumnEffect,
      r.reference.raketechReported, r.reference.juneDifference,
      r.reference.juneComments.join(' | '),
      r.reference.julyStatedAdwinEarnings ?? '', r.tailWrittenDown,
      r.newToThisReport ? 'yes' : 'no', r.missingFromAdwstats ? 'no' : 'yes',
    ]);
    const csv = [header, ...lines]
      .map((row) => row.map((cell) => {
        const s = String(cell ?? '');
        return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
      }).join(','))
      .join('\n');

    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `raketech-claim-v4-${rowTab}-${lastMonth}.csv`;
    a.click();
    URL.revokeObjectURL(url);
    toast.success(`Exported ${claimRows.length} accounts`);
  };

  /**
   * The same rows one month at a time, which is the form Raketech asked for:
   * the account, the period and every figure held for it side by side. Months
   * with nothing on either side are left out.
   */
  const exportMonths = () => {
    if (claimRows.length === 0) { toast.error('Nothing to export'); return; }
    const header = [
      'Account ID', 'AC IDs', 'Partner', 'Brands', 'Geo', 'Month',
      'Invoiced', 'FTDs', 'Earned (claim basis)', 'Difference',
      'June 2025 file, Adwin earnings',
      // Their reported column is an account total with no months behind it, so
      // the month shape is inferred. Said in the header rather than left to be
      // discovered by whoever reads the file.
      'June 2025 file, Reported (EUR), account total spread pro rata',
      'Their July 2026 report', 'Operator backend, net of 5%', 'Affiliation Cloud (reference)',
      'January 2025 file (not claimed)', 'Their comment',
    ];
    const lines: (string | number)[][] = [];
    for (const r of claimRows) {
      const comment = r.reference.juneComments.join(' | ');
      for (const m of r.monthly) {
        if (m.invoiced === 0 && m.earned === 0 && m.juneRecon === 0 && m.restated === 0) continue;
        lines.push([
          r.accountId, r.acIds.join(' '), r.partner, r.brands.join(' / '), r.geos.join(' '), m.month,
          m.invoiced, m.ftds, m.earned, Math.round((m.earned - m.invoiced) * 100) / 100,
          m.juneRecon, m.juneReported, m.restated, m.synced, m.ac, m.januaryFile, comment,
        ]);
      }
    }
    const csv = [header, ...lines]
      .map((row) => row.map((cell) => {
        const s = String(cell ?? '');
        return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
      }).join(','))
      .join('\n');

    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `raketech-claim-v4-${rowTab}-by-month-${lastMonth}.csv`;
    a.click();
    URL.revokeObjectURL(url);
    toast.success(`Exported ${lines.length} account months`);
  };

  // -------------------------------------------------------------------------
  if (isLoading && !claim) {
    return (
      <div className="min-h-screen bg-[hsl(225,15%,6%)] flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="h-8 w-8 text-amber-500 animate-spin mx-auto mb-3" />
          <p className="text-sm text-zinc-400">Rebuilding the claim on their new numbers…</p>
        </div>
      </div>
    );
  }

  if (error && !claim) {
    return (
      <div className="min-h-screen bg-[hsl(225,15%,6%)] p-8">
        <div className="max-w-xl mx-auto bg-red-500/5 border border-red-500/20 rounded-xl p-6">
          <div className="flex items-start gap-3">
            <AlertTriangle className="h-5 w-5 text-red-400 shrink-0 mt-0.5" />
            <div>
              <h2 className="text-sm font-semibold text-white mb-1">Could not build the report</h2>
              <p className="text-xs text-zinc-400 mb-4">{error}</p>
              <button
                onClick={load}
                className="px-3 py-1.5 text-xs bg-[hsl(225,15%,14%)] hover:bg-[hsl(225,15%,18%)] text-zinc-200 rounded-lg border border-[hsl(225,15%,22%)]"
              >
                Try again
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (!claim || !dataset || !june) return null;

  const { summary } = claim;
  const controls = dataset.controls;
  const theirClaim = Math.abs(controls.underpaymentAfterFees ?? 0);

  return (
    <div className="min-h-screen bg-[hsl(225,15%,6%)] p-4 sm:p-6 lg:p-8">
      {/* Header */}
      <div className="mb-6">
        <div className="flex flex-wrap items-start justify-between gap-4">
          <div>
            <div className="flex items-center gap-2 mb-1">
              <h1 className="text-xl font-semibold text-white">Final Report v4</h1>
              <span className="px-2 py-0.5 text-[10px] rounded border border-sky-500/30 bg-sky-500/10 text-sky-400">
                Every invoice line placed once
              </span>
              {claim?.summary.attribution && (
                <button
                  onClick={() => setShowAttribution((v) => !v)}
                  className={`px-2 py-0.5 text-[10px] rounded border transition-colors ${
                    claim.summary.attribution.balanced
                      ? 'border-emerald-500/30 bg-emerald-500/10 text-emerald-400 hover:bg-emerald-500/20'
                      : 'border-red-500/30 bg-red-500/10 text-red-400'
                  }`}
                  title="How Adwin's invoice detail was placed against Raketech's accounts"
                >
                  {claim.summary.attribution.balanced ? 'Detail balances' : 'Detail does not balance'}
                  {' · '}{claim.summary.attribution.unattributedLines} unplaced
                  {' · '}{claim.summary.attribution.judgedLines} on size
                </button>
              )}
              {acMonthlyByAccountId.size > 0 && (
                <span
                  className="px-2 py-0.5 text-[10px] rounded border border-emerald-500/30 bg-emerald-500/10 text-emerald-400"
                  title={`Cross-checking ${acMonthlyByAccountId.size} accounts against Affiliation Cloud`}
                >
                  AC cross-check on
                </span>
              )}
            </div>
            <p className="text-xs text-zinc-500 max-w-3xl leading-relaxed">
              Each account is valued at the strongest figure available — the "Adwin earnings"
              column of their June 2025 workbook, what we synced from the operator, a manual
              backend check, or their July 2026 restatement — and compared to what Adwin
              invoiced. v4 changes where the invoicing comes from: every line of Adwin's
              invoice detail is placed once, on one account, across every account either
              report names, by the deal code on the line first. v3 matched only against the
              June 2025 accounts, so accounts created from December 2024 read as never invoiced.
              v3 remains untouched at{' '}<span className="text-zinc-400">/raketech/final-report-v3</span>.
            </p>
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <label className="flex items-center gap-2 px-3 py-1.5 text-xs text-zinc-300 bg-[hsl(225,15%,12%)] border border-[hsl(225,15%,20%)] rounded-lg cursor-pointer">
              <Calendar className="h-3.5 w-3.5 text-zinc-500" />
              <span className="text-zinc-500">Through</span>
              <select
                value={lastMonth}
                onChange={(e) => setLastMonth(e.target.value)}
                className="bg-transparent text-zinc-200 outline-none cursor-pointer"
              >
                {dataset.months.map((m) => (
                  <option key={m} value={m} className="bg-[hsl(225,15%,12%)]">{fmtMonth(m)}</option>
                ))}
              </select>
            </label>
            <label className="flex items-center gap-2 px-3 py-1.5 text-xs text-zinc-300 bg-[hsl(225,15%,12%)] border border-[hsl(225,15%,20%)] rounded-lg cursor-pointer">
              <input
                type="checkbox"
                checked={applyMargin}
                onChange={(e) => setApplyMargin(e.target.checked)}
                className="accent-amber-500"
              />
              Net their 5% margin
            </label>
            <button
              onClick={exportCsv}
              className="flex items-center gap-1.5 px-3 py-1.5 text-xs text-zinc-300 hover:text-white bg-[hsl(225,15%,12%)] hover:bg-[hsl(225,15%,16%)] border border-[hsl(225,15%,20%)] rounded-lg transition-colors"
            >
              <Download className="h-3.5 w-3.5" />
              Export accounts
            </button>
            <button
              onClick={exportMonths}
              className="flex items-center gap-1.5 px-3 py-1.5 text-xs text-zinc-300 hover:text-white bg-[hsl(225,15%,12%)] hover:bg-[hsl(225,15%,16%)] border border-[hsl(225,15%,20%)] rounded-lg transition-colors"
            >
              <Download className="h-3.5 w-3.5" />
              Export months
            </button>
            <button
              onClick={() => { setShareSelection(null); setShareTitle('Adwin — working reconciliation position'); setShareDesc(''); setSavedUrl(null); setShowShare(true); }}
              className="flex items-center gap-1.5 px-3 py-1.5 text-xs text-amber-400 hover:text-amber-300 bg-amber-500/10 hover:bg-amber-500/15 border border-amber-500/30 rounded-lg transition-colors"
            >
              <Share2 className="h-3.5 w-3.5" />
              Save &amp; share
              {snapshots.length > 0 && <span className="text-amber-500/70">({snapshots.length})</span>}
            </button>
            <button
              onClick={load}
              className="flex items-center gap-1.5 px-3 py-1.5 text-xs text-zinc-300 hover:text-white bg-[hsl(225,15%,12%)] hover:bg-[hsl(225,15%,16%)] border border-[hsl(225,15%,20%)] rounded-lg transition-colors"
            >
              <RefreshCw className={`h-3.5 w-3.5 ${isLoading ? 'animate-spin' : ''}`} />
              Refresh
            </button>
          </div>
        </div>
      </div>

      {showAttribution && attribution && (
        <AttributionPanel data={attribution} onClose={() => setShowAttribution(false)} />
      )}

      {/* Headline */}
      <div className="grid grid-cols-2 lg:grid-cols-5 gap-3 mb-6">
        <StatCard
          label="They admit"
          value={fmtEUR(theirClaim)}
          hint="Their own bottom line"
          icon={<Scale className="h-4 w-4" />}
          color="zinc"
        />
        <StatCard
          label={rowTab === 'overpaid' ? 'Overpaid to them' : rowTab === 'all' ? 'Net position' : 'This report claims'}
          value={fmtEUR(Math.abs(claimTotals.underpaid))}
          hint={`${claimTotals.count} ${rowTab === 'all' ? 'accounts, netted' : `${rowTab} accounts`}`
            + ' — account by account, before the basis and scope set on What is outstanding'}
          icon={<AlertTriangle className="h-4 w-4" />}
          color={ROW_TABS[rowTab].tone === 'zinc' ? 'zinc' : ROW_TABS[rowTab].tone}
        />
        <StatCard
          label="Proved by their restatement"
          value={fmtEUR(claimTotals.restatement)}
          hint="Shortfall from their own new figures"
          icon={<FileText className="h-4 w-4" />}
          color="amber"
        />
        <StatCard
          label="Earned, claimed accounts"
          value={fmtEUR(claimTotals.earned)}
          hint="Best evidence per account"
          icon={<Sigma className="h-4 w-4" />}
          color="emerald"
        />
        <StatCard
          label="Invoiced, same accounts"
          value={fmtEUR(claimTotals.invoiced)}
          hint="What they billed"
          icon={<Database className="h-4 w-4" />}
          color="cyan"
        />
      </div>

      {/* What changed from v2, stated up front rather than left to be discovered. */}
      <div className="mb-6 bg-sky-500/[0.06] border border-sky-500/20 rounded-lg px-4 py-3">
        <div className="flex items-start gap-3">
          <FileText className="h-4 w-4 text-sky-400 shrink-0 mt-0.5" />
          <p className="text-xs text-zinc-400 leading-relaxed">
            <span className="text-zinc-200">Correcting the source cost this claim
              {' '}{fmtEURExact(summary.januaryColumnEffect)}.</span>{' '}
            v2 valued {claim.rows.filter((r) => r.januaryColumnEffect > 1).length} accounts at the
            January 2025 file and reached {fmtEUR(Math.abs(summary.underpaidOnJanuaryColumn))} net;
            on Raketech's own June column the same accounts come to
            {' '}{fmtEUR(Math.abs(summary.underpaidTotal))} net, or
            {' '}<span className="text-red-400">{fmtEUR(Math.abs(summary.underpaidGross))}</span> before
            their {fmtEUR(summary.overpaidGross)} of overpaid accounts is netted against it.
            {' '}{summary.accountsInJuneRecon} of {summary.accounts} accounts appear in the June workbook.
          </p>
        </div>
      </div>

      {/* What the evidence does not cover. Read this before the totals. */}
      <div className="mb-6 bg-amber-500/[0.06] border border-amber-500/20 rounded-lg px-4 py-3">
        <div className="flex items-start gap-3">
          <AlertTriangle className="h-4 w-4 text-amber-400 shrink-0 mt-0.5" />
          <div className="text-xs text-zinc-400 leading-relaxed space-y-1.5">
            <p>
              <span className="text-zinc-200">
                {summary.coverage.gapMonths} months across {summary.coverage.accountsWithGaps} accounts
                carry an Adwin invoice and no reported earnings at all
                — {fmtEURExact(summary.coverage.gapInvoiced)} of invoicing judged against nothing.
              </span>{' '}
              Of that, {fmtEUR(summary.coverage.gapReportedElsewhere)} is a month where the same brand
              does appear in their files under a different account id, and
              {' '}<span className="text-amber-400">{fmtEUR(summary.coverage.gapAbsent)}</span> is a
              month their files do not describe at all.
            </p>
            <p>
              {fmtEUR(summary.coverage.gapOnOverpaidAccounts)} of it sits on accounts this report
              currently reads as overpaid, which is that much of the
              {' '}{fmtEUR(summary.overpaidGross)} overpaid total resting on an absence of data rather
              than on evidence of overpayment. Another
              {' '}{fmtEUR(summary.coverage.gapOnUnderpaidAccounts)} sits on accounts already underpaid,
              where it depresses a shortfall that is otherwise proved.
            </p>
            <p>
              Grouping the accounts back up by partner — so that an account Raketech moved between ids
              cannot show as a shortfall and an overpayment at the same time — takes the gross overpaid
              figure from {fmtEUR(summary.overpaidGross)} to
              {' '}{fmtEUR(summary.grouped.byPartner.overpaidGross)}. The net is unchanged either way.
            </p>
            {summary.earningsBeyondInvoiceData > 1 && (
              <p>
                <span className="text-zinc-200">
                  Invoice line detail runs to {summary.invoiceDataThrough}, but the claim runs to
                  {' '}{lastMonth}.
                </span>{' '}
                {fmtEURExact(summary.earningsBeyondInvoiceData)} of earnings therefore stands with no
                invoicing deducted against it, and invoices 20331 and 20332 — settled on 31 July 2026 —
                bill those same months. The statement takes the overlap out so it is not claimed on
                top of what was already paid; add the per-account detail for those months to the
                commission sheet and it comes back properly matched.
              </p>
            )}
            <p>
              Taking the {fmtEUR(summary.overpaidGross)} of overpayment apart month by month:
              {' '}<span className="text-amber-400">{fmtEURExact(summary.coverage.blankMonthOverpaid)}</span>{' '}
              of it is months they leave blank and
              {' '}<span className="text-amber-400">{fmtEURExact(summary.coverage.partialMonthOverpaid)}</span>{' '}
              is months they report at under half of what was invoiced. Only
              {' '}<span className="text-zinc-200">{fmtEURExact(summary.coverage.evidencedOverpaid)}</span>{' '}
              is a month their own figures actually describe, and so the only part that is evidence of
              overpayment rather than of their reporting.
            </p>
            {summary.duplicateInvoiceRevenueRemoved > 1 && (
              <p>
                {fmtEURExact(summary.duplicateInvoiceRevenueRemoved)} of invoicing matched two of
                their accounts at once, because Adwin's line detail is held against a deal label and a
                brand rather than against their account id. Each line is now deducted once, from the
                account whose own offer is the deal it names, so an account is no longer credited with
                invoicing raised for another
                {summary.duplicateInvoiceRevenueRemaining > 1
                  ? `; ${fmtEURExact(summary.duplicateInvoiceRevenueRemaining)} still matches on more than one row`
                  : ' — nothing is left matching on more than one row'}.
              </p>
            )}
          </div>
        </div>
      </div>

      {/* Tabs */}
      <div className="flex flex-wrap items-center gap-1.5 mb-4 border-b border-[hsl(225,15%,15%)] pb-3">
        {([
          ['underpaid', `Underpaid (${tabCounts.underpaid})`],
          ['overpaid', `Overpaid (${tabCounts.overpaid})`],
          ['all', `All accounts (${tabCounts.all})`],
          ['outstanding', 'What is outstanding'],
          ['coverage', `Gaps (${summary.coverage.accountsWithGaps})`],
          ['bridge', 'Their number vs ours'],
          ['sources', 'Evidence used'],
          ['june', 'Their June file'],
          ['unverifiable', `Unverifiable (${evidenceCounts.unverifiable})`],
          ['access', `No access (${evidenceCounts.noLogin})`],
          ['pano', `PA = NO (${evidenceCounts.paNo})`],
          ['writedowns', `Write-downs (${evidenceCounts.writeDowns})`],
        ] as [MainTab, string][]).map(([key, label]) => (
          <button
            key={key}
            onClick={() => setActiveTab(key)}
            className={`px-3 py-1.5 text-xs rounded-md whitespace-nowrap transition-colors ${
              activeTab === key
                ? 'bg-amber-500/15 text-amber-400 border border-amber-500/25'
                : 'text-zinc-400 hover:text-zinc-200 hover:bg-[hsl(225,15%,12%)] border border-transparent'
            }`}
          >
            {label}
          </button>
        ))}
      </div>

      {/* ------------------------------------------------------------------ */}
      {/* The claim */}
      {/* ------------------------------------------------------------------ */}
      {isRowTab(activeTab) && (
        <div className="space-y-4 animate-fade-in">
          <Explainer>
            {rowTab === 'underpaid' && (
              <>
                One row per underpaid account, largest shortfall first. Earnings up to {fmtMonth(RECON_END)} come
                from whichever of the four sources is highest — their June 2025 credited column, our sync, a
                manual check, or their July 2026 restatement; from {fmtMonth('2024-12')} onward they come from
                Raketech's own monthly columns, which is why this report no longer needs a live Affiliation
                Cloud session to reach the recent period. Accounts they overpaid are on their own tab rather
                than being allowed to offset here — netting them off is the main reason their bottom line is
                smaller than this one.
              </>
            )}
            {rowTab === 'overpaid' && (
              <>
                Accounts where Raketech invoiced more than we can evidence was earned. These are shown for
                completeness and are deliberately kept out of the claim: offsetting them against underpaid
                accounts is precisely the step that shrinks their bottom line to {fmtEURExact(theirClaim)}. An
                account can also land here simply because we hold no data for it, so treat this as a list to
                review rather than a debt. It is also Raketech's counter-exposure, so it is worth knowing
                before the claim goes out: most of these accounts were invoiced on the monthly figures
                Raketech reported at the time, which their own June workbook now credits lower.
              </>
            )}
            {rowTab === 'all' && (
              <>
                Every account in either report, largest absolute difference first, with underpaid and overpaid
                netted against each other. This is the closest view to how Raketech present their own figure,
                and the gap between this net total and the underpaid tab is the value of their netting.
              </>
            )}
          </Explainer>

          <div className="flex flex-wrap items-center gap-2">
            <input
              type="text"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              placeholder="Filter by partner, brand, account id…"
              className="px-3 py-1.5 text-xs bg-[hsl(225,15%,10%)] border border-[hsl(225,15%,20%)] rounded-lg text-zinc-200 placeholder:text-zinc-600 outline-none focus:border-amber-500/40 w-72"
            />
            <label className="flex items-center gap-2 px-3 py-1.5 text-xs text-zinc-300 bg-[hsl(225,15%,10%)] border border-[hsl(225,15%,20%)] rounded-lg cursor-pointer">
              <input
                type="checkbox"
                checked={onlyRestatement}
                onChange={(e) => setOnlyRestatement(e.target.checked)}
                className="accent-amber-500"
              />
              Only accounts their restatement exposes
            </label>
          </div>

          <div className="bg-[hsl(225,15%,10%)] rounded-xl border border-[hsl(225,15%,18%)] overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full text-xs">
                <thead className="bg-[hsl(225,15%,8%)]">
                  <tr>
                    <th className="w-6" />
                    <th className="text-left px-3 py-2 text-zinc-500 font-medium">Partner / Brands</th>
                    <th className="text-left px-3 py-2 text-zinc-500 font-medium">Account</th>
                    <th className="text-left px-3 py-2 text-zinc-500 font-medium">Evidence</th>
                    <th className="text-right px-3 py-2 text-zinc-500 font-medium">Invoiced</th>
                    <th className="text-right px-3 py-2 text-zinc-500 font-medium">Earned</th>
                    <th className="text-right px-3 py-2 text-zinc-500 font-medium">Dec 24 →</th>
                    <th className="text-right px-3 py-2 text-zinc-500 font-medium">
                      {rowTab === 'underpaid' ? 'Shortfall' : 'Difference'}
                    </th>
                    <th className="text-right px-3 py-2 text-zinc-500 font-medium">%</th>
                    <th className="text-right px-3 py-2 text-zinc-500 font-medium">From restatement</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-[hsl(225,15%,14%)]">
                  {claimRows.slice(0, 400).map((row) => (
                    <React.Fragment key={row.key}>
                      <tr
                        className="hover:bg-[hsl(225,15%,12%)] cursor-pointer"
                        onClick={() => toggleRow(row.key)}
                      >
                        <td className="px-2 py-2 text-zinc-600">
                          {expanded.has(row.key)
                            ? <ChevronDown className="h-3.5 w-3.5" />
                            : <ChevronRight className="h-3.5 w-3.5" />}
                        </td>
                        <td className="px-3 py-2">
                          <div className="text-zinc-200 truncate max-w-[200px]">{row.partner || '—'}</div>
                          <div className="text-[10px] text-zinc-500 truncate max-w-[200px]">
                            {row.brands.slice(0, 3).join(', ') || '—'}
                            {row.brands.length > 3 && ` +${row.brands.length - 3}`}
                          </div>
                        </td>
                        <td className="px-3 py-2">
                          <div className="text-[10px] text-zinc-400">
                            {row.accountId || '—'}
                            {row.newToThisReport && (
                              <span className="text-amber-400/80"> · only in their reports</span>
                            )}
                          </div>
                          <div className="text-[10px] text-zinc-600">
                            {row.acIds.length > 0 ? `AC ${row.acIds.join(', ')}` : 'no AC account'}
                          </div>
                        </td>
                        <td className="px-3 py-2">
                          <span className={`inline-flex px-1.5 py-0.5 rounded border text-[10px] font-medium ${SOURCE_STYLES[row.chosenSource]}`}>
                            {SOURCE_SHORT[row.chosenSource]}
                          </span>
                        </td>
                        <td className="px-3 py-2 text-right tabular-nums text-zinc-300">{fmtEUR(row.invoicedTotal)}</td>
                        <td className="px-3 py-2 text-right tabular-nums text-emerald-400">{fmtEUR(row.earnedTotal)}</td>
                        <td className="px-3 py-2 text-right tabular-nums text-purple-400/80">
                          {row.earnedTail ? fmtEUR(row.earnedTail) : '—'}
                        </td>
                        <td className={`px-3 py-2 text-right tabular-nums font-medium ${
                          row.underpaid < -1 ? 'text-red-400'
                            : row.underpaid > 1 ? 'text-amber-400' : 'text-zinc-500'
                        }`}>
                          {fmtEUR(row.underpaid)}
                        </td>
                        <td className={`px-3 py-2 text-right tabular-nums text-[10px] ${
                          row.underpaid < -1 ? 'text-red-500/80'
                            : row.underpaid > 1 ? 'text-amber-500/80' : 'text-zinc-600'
                        }`}>
                          {row.underpaidPct.toFixed(1)}%
                        </td>
                        <td className="px-3 py-2 text-right tabular-nums text-amber-400/90">
                          {row.attributableToRestatement > 1 ? fmtEUR(row.attributableToRestatement) : '—'}
                        </td>
                      </tr>

                      {expanded.has(row.key) && (
                        <tr className="bg-[hsl(225,15%,8%)]">
                          <td />
                          <td colSpan={9} className="px-3 py-3">
                            <ClaimDetail row={row} />
                          </td>
                        </tr>
                      )}
                    </React.Fragment>
                  ))}
                </tbody>
              </table>
            </div>
            {claimRows.length > 400 && (
              <div className="px-4 py-2 text-[11px] text-zinc-500 border-t border-[hsl(225,15%,15%)]">
                Showing the largest 400 of {claimRows.length}. Export the CSV for the full list.
              </div>
            )}
            {claimRows.length === 0 && (
              <div className="p-8 text-center text-sm text-zinc-500">
                No {rowTab === 'all' ? '' : `${rowTab} `}accounts match this filter.
              </div>
            )}
            {claimRows.length > 0 && (
              <div className="px-4 py-3 border-t border-[hsl(225,15%,15%)] flex flex-wrap items-center justify-between gap-3 text-xs">
                <span className="text-zinc-500">
                  <strong className="text-white">{claimTotals.count}</strong>
                  {' '}{rowTab === 'all' ? 'accounts' : `${rowTab} accounts`} through {fmtMonth(lastMonth)}
                </span>
                <div className="flex flex-wrap items-center gap-4">
                  <span className="text-zinc-500">Earned <span className="text-emerald-400 tabular-nums">{fmtEUR(claimTotals.earned)}</span></span>
                  <span className="text-zinc-500">Invoiced <span className="text-zinc-300 tabular-nums">{fmtEUR(claimTotals.invoiced)}</span></span>
                  <span className="text-zinc-500">
                    {rowTab === 'underpaid' ? 'Shortfall' : rowTab === 'overpaid' ? 'Overpaid' : 'Net'}
                    {' '}
                    <span className={`tabular-nums font-bold ${
                      claimTotals.underpaid < 0 ? 'text-red-400' : 'text-amber-400'
                    }`}>
                      {fmtEUR(claimTotals.underpaid)}
                    </span>
                  </span>
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {/* ------------------------------------------------------------------ */}
      {/* What is outstanding */}
      {/* ------------------------------------------------------------------ */}
      {activeTab === 'outstanding' && outstanding && (
        <div className="space-y-4 animate-fade-in">
          <Explainer>
            Four things are owed and they answer to different documents, so they are kept apart. The
            commission shortfall answers to the reconciliation, and everything already received against
            it — invoice 20023 and the fee lines credited on invoice 20318 — comes off it. Commission
            Raketech's monthly reports recorded as earned and Adwin never billed in full answers to those
            reports set against Raketech's own ledger of Adwin's invoices, month by month, in the table at
            the foot of this tab. The flat fee answers to clause 4.3.1 of the agreement — EUR 200,000
            payable within twelve months of signature, in addition to the Net Revenue, CPA and CPC fees —
            and nothing in the reconciliation can reduce it. Unpaid invoices answer to themselves. Neither
            the flat fee nor the transaction cost Raketech undertook to compensate has come back in full.
          </Explainer>

          <div className="bg-[hsl(225,15%,9%)] border border-[hsl(225,15%,16%)] rounded-lg px-4 py-3">
            <div className="text-[11px] uppercase tracking-wide text-zinc-500 mb-2">
              What the shortfall counts
            </div>
            <div className="flex flex-wrap gap-2">
              {([
                ['net', 'Net of overpayments',
                  'Accounts they overpaid are allowed to discharge accounts they underpaid. Their '
                  + 'basis, and the most conservative one.'],
                ['netEvidenced', 'Net of evidenced overpayments',
                  'The same, except an overpayment only nets as far as their own file reports the '
                  + 'months it rests on. A month they leave blank is not evidence that Adwin was '
                  + 'overpaid, so it cannot pay for a shortfall elsewhere.'],
                ['byPartner', 'Shortfalls only, by partner',
                  'Counts only accounts they underpaid. Accounts are grouped by partner first, so '
                  + 'an account they re-mapped between ids is put back together and cannot show as '
                  + 'a shortfall and an overpayment at once.'],
                ['byPartnerEvidenced', 'By partner, evidenced offsets only',
                  'Both corrections at once: grouped by partner so nothing is counted twice, and '
                  + 'within the group an overpayment offsets a shortfall only as far as their own '
                  + 'file reports the months it rests on. The position this report defends.'],
                ['byAccount', 'Shortfalls only, by account',
                  'Counts only accounts they underpaid, account by account, with no grouping. The '
                  + 'highest reading, and the one most exposed to their re-mapping.'],
              ] as const).map(([key, label, body]) => (
                <button
                  key={key}
                  onClick={() => setClaimScope(key)}
                  title={body}
                  className={`px-3 py-1.5 rounded-lg text-xs border transition-colors ${
                    claimScope === key
                      ? 'border-amber-500/40 bg-amber-500/10 text-amber-200'
                      : 'border-[hsl(225,15%,18%)] text-zinc-400 hover:text-zinc-200'
                  }`}
                >
                  {label}
                  <span className="ml-2 tabular-nums text-zinc-500">
                    {fmtEUR(summary.band[claimBasis][key])}
                  </span>
                </button>
              ))}
            </div>
            <p className="text-[11px] text-zinc-500 leading-relaxed mt-2">
              Netting hands them {fmtEURExact(summary.overpaidGross)} of credit on accounts they
              overpaid. Only {fmtEURExact(summary.coverage.evidencedOverpaid)} of that rests on months
              their own file actually reports: {fmtEUR(summary.coverage.blankMonthOverpaid)} is months
              they leave blank and {fmtEUR(summary.coverage.partialMonthOverpaid)} is months they
              report at under half of what was invoiced. On the like-for-like basis the blank months
              are already out of the comparison, so "evidenced" and "all" come to the same thing there.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
            <BandCard
              active={claimBasis === 'likeForLike'}
              onClick={() => setClaimBasis('likeForLike')}
              title="Like for like"
              amount={summary.band.likeForLike[claimScope]}
              body={'Only the months their own figures describe are compared, on both sides. A month '
                + 'they left blank comes out of the comparison instead of being read as zero earnings, '
                + `and the ${fmtEURExact(summary.band.undescribedInvoiced)} Adwin invoiced in those `
                + 'months is set aside with it. The only basis that treats their silence the same way '
                + 'in both directions.'}
            />
            <BandCard
              active={claimBasis === 'stated'}
              onClick={() => setClaimBasis('stated')}
              title="Account by account"
              amount={summary.band.stated[claimScope]}
              body={'Each account judged on the single source of theirs that reports it highest, over '
                + 'the whole period. A month one of their files leaves blank counts as zero earnings, '
                + 'which reads their silence in their favour.'}
            />
            <BandCard
              active={claimBasis === 'monthLevel'}
              onClick={() => setClaimBasis('monthLevel')}
              title="Month by month"
              amount={summary.band.monthLevel[claimScope]}
              body={'The same, except each month is judged on whichever of their files covers it. A file '
                + 'that is silent in a month no longer suppresses one that is not. Adds '
                + `${fmtEURExact(summary.band.monthLevel[claimScope] - summary.band.stated[claimScope])} — `
                + 'every euro of it a month they themselves report.'}
            />
            <div className="rounded-xl border border-dashed border-amber-500/30 bg-amber-500/[0.03] px-4 py-3">
              <div className="text-[11px] uppercase tracking-wide text-amber-400/80">Claimed on no basis</div>
              <div className="text-xl font-semibold text-amber-300 tabular-nums mt-1">
                {fmtEURExact(summary.band.undescribedInvoiced)}
              </div>
              <p className="text-[11px] text-zinc-500 leading-relaxed mt-1.5">
                Invoiced in months no file of theirs describes.
                {' '}{fmtEUR(summary.band.unresolvedRemapping)} of that invoicing is a month where the
                same brand does appear under another account id, so it turns on the mapping table they
                have not supplied. Separately, {fmtEUR(summary.coverage.partialWedge)} sits in months
                they do describe but at under half of what was invoiced, and every basis here takes
                their figure for those months as it stands.
              </p>
            </div>
          </div>

          <div className="bg-[hsl(225,15%,9%)] border border-[hsl(225,15%,16%)] rounded-lg px-4 py-3">
            <div className="text-[11px] uppercase tracking-wide text-zinc-500 mb-2">
              How this reaches the figure on the Accounts tab
            </div>
            <div className="space-y-1.5 text-xs">
              {([
                ['Net position, every account added together',
                  Math.abs(summary.underpaidTotal),
                  'What the All accounts tab totals: every shortfall and every overpayment in one '
                  + 'sum, account by account, on each account\'s own best figure.'],
                [claimBasis === 'likeForLike' ? 'Basis: compare only the months they describe'
                  : claimBasis === 'monthLevel' ? 'Basis: value each month on whichever file covers it'
                    : 'Basis: value each account once, over the whole period',
                  summary.band[claimBasis].net - summary.band.stated.net,
                  'The difference the chosen basis makes, before anything is grouped.'],
                [claimScope === 'net' ? 'Scope: overpayments net in full'
                  : claimScope === 'netEvidenced' ? 'Scope: only evidenced overpayments net'
                    : claimScope === 'byPartner' ? 'Scope: shortfalls only, grouped by partner'
                      : claimScope === 'byPartnerEvidenced' ? 'Scope: by partner, evidenced offsets only'
                        : 'Scope: shortfalls only, account by account',
                  summary.band[claimBasis][claimScope] - summary.band[claimBasis].net,
                  'What declining to net one account\'s overpayment against another\'s shortfall is worth.'],
              ] as const).map(([label, amount, note], i) => (
                <div key={i} className="flex items-baseline justify-between gap-4">
                  <div>
                    <span className="text-zinc-300">{label}</span>
                    <p className="text-[11px] text-zinc-600 leading-relaxed">{note}</p>
                  </div>
                  <span className={`tabular-nums shrink-0 ${i === 0 ? 'text-zinc-300' : 'text-sky-400'}`}>
                    {i === 0 ? '' : '+'}{fmtEURExact(amount)}
                  </span>
                </div>
              ))}
              <div className="flex items-baseline justify-between gap-4 pt-1.5 border-t border-[hsl(225,15%,16%)]">
                <span className="text-white font-medium">Commission shortfall claimed, first line below</span>
                <span className="tabular-nums text-red-400 font-semibold">
                  {fmtEURExact(summary.band[claimBasis][claimScope])}
                </span>
              </div>
            </div>
          </div>

          <div className="bg-[hsl(225,15%,10%)] rounded-xl border border-[hsl(225,15%,18%)] overflow-hidden divide-y divide-[hsl(225,15%,14%)]">
            {outstanding.lines.map((line, i) => <OutstandingRow key={i} line={line} />)}
          </div>

          <div className="bg-[hsl(225,15%,9%)] border border-[hsl(225,15%,16%)] rounded-lg px-4 py-3 space-y-2">
            <div className="text-[11px] uppercase tracking-wide text-zinc-500">
              The {fmtEURExact(SETTLEMENT_FACTS.feeLinesCreditedAsPartPayment)} of fee lines credited on 20318
            </div>
            <div className="flex flex-wrap gap-2">
              {([
                ['part_payment', 'Part payment of the underpayment'],
                ['flat_fee', 'Instalments of the flat fee'],
              ] as const).map(([key, label]) => (
                <button
                  key={key}
                  onClick={() => setCreditedFeeLines(key)}
                  className={`px-3 py-1.5 rounded-lg text-xs border transition-colors ${
                    creditedFeeLines === key
                      ? 'border-amber-500/40 bg-amber-500/10 text-amber-200'
                      : 'border-[hsl(225,15%,18%)] text-zinc-400 hover:text-zinc-200'
                  }`}
                >
                  {label}
                </button>
              ))}
            </div>
            <p className="text-xs text-zinc-400 leading-relaxed">
              {creditedFeeLines === 'part_payment'
                ? 'Five lines on invoices 20033 to 20146, the month after Raketech proposed repaying the '
                  + 'commission error over twelve months and covering the transaction cost it had '
                  + 'deducted. On this reading they are that repayment, billed under the fee label, so '
                  + 'crediting them on 20318 was right and they come off the commission claim.'
                : 'On this reading the five lines were clause 4.3.1 instalments, so crediting them on '
                  + '20318 discharged a debt they never related to and the commission claim keeps them.'}
              {' '}Either way the flat fee balance is
              unchanged at {fmtEURExact(SETTLEMENT_FACTS.flatFeeContract - SETTLEMENT_FACTS.flatFeeRetained)}:
              on the first reading it never received the money, on the second it received it and gave it
              straight back.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <FactCard
              title="The flat fee, clause 4.3.1"
              rows={[
                ['Payable in the aggregate', SETTLEMENT_FACTS.flatFeeContract],
                ['Billed as "Affiliate marketing fee"', SETTLEMENT_FACTS.flatFeeInvoiced],
                ['Credited back on 20318', -SETTLEMENT_FACTS.feeLinesCreditedAsPartPayment],
                ['Actually kept against the fee', SETTLEMENT_FACTS.flatFeeRetained],
                ['Never delivered', SETTLEMENT_FACTS.flatFeeContract - SETTLEMENT_FACTS.flatFeeRetained],
              ]}
              note={`Due by ${SETTLEMENT_FACTS.flatFeeDueBy}, twelve months from signature. The fee lines `
                + 'began in September 2024 and stopped at invoice 20146 on 30 June 2025 — the month the '
                + 'twelve-month window closed — leaving the balance unbilled and undelivered.'}
            />
            <FactCard
              title="The transaction cost, undertaking of 9 May 2025"
              rows={[
                ['To be compensated in full', SETTLEMENT_FACTS.transactionFeeToCompensate],
                ['Of which billed as "Reconciliation fee" and paid', SETTLEMENT_FACTS.reconciliationFeeInvoiced],
                ['Whole 5% waived since May 2025, on their gross', outstanding?.lines.find((l) => l.label.startsWith('Less compensated through the 5%'))?.amount ?? 0],
                ['Still outstanding, claimed below', outstanding?.transactionCost ?? 0],
              ]}
              note={'Raketech undertook to stop taking its share until the whole cost had been '
                + 'compensated. The waiver is counted in full here — the billed part and the part inside '
                + 'the commission shortfall, which values the months from May 2025 gross — and the balance '
                + 'is claimed as its own head in the statement. Raketech\'s letter of 9 August 2026 nets '
                + 'the same cost against commission owed and reserves a recharge; neither is accepted.'}
            />
            <FactCard
              title="The invoicing the claim deducts"
              rows={[
                ['Matched invoice detail in this report', summary.invoicedTotal],
                ['Of it, deducted on this basis', claimBasis === 'likeForLike'
                  ? summary.describedInvoicedTotal : summary.invoicedTotal],
                ['Commission Adwin actually billed', SETTLEMENT_FACTS.commissionBilled
                  + (summary.invoiceDataThrough >= '2026-05' ? 34630.48 : 0)
                  + (summary.invoiceDataThrough >= '2026-06' ? 34955.74 : 0)],
                ['Reported, invoiced on, never billed in full', tieOut ? tieOut.neverBilled : 0],
              ]}
              note={'81 invoices, headers net of the fee lines inside them, excluding 20023 and 20318, '
                + 'plus 20331 and 20332 once the claim reaches the months they bill. Includes the EUR 131,000 '
                + 'of invoice 19878 — "Publisher Costs 07/2024 W2 / Outstanding from error" on Raketech\'s own '
                + 'ledger, settled 23 August 2024, later credited and re-issued as 20090. Adwin\'s register and '
                + 'Raketech\'s Finance ledger agree on every invoice but two from March 2023. The monthly reports '
                + 'the claim deducts exceed what was billed by the last figure; the table below places it month '
                + 'by month on Raketech\'s own ledger.'}
            />
            <FactCard
              title="Reported, invoiced, then written down"
              rows={[
                ['Inside the claim on their reported column', summary.reportedWriteDown],
                ['Of it, annotated in their own comment column', summary.reportedWriteDownCommented],
              ]}
              note={`Across ${summary.reportedWriteDownAccounts} accounts. `
                + 'Their June 2025 workbook carries "Reported (EUR)" — what their monthly reports '
                + 'paid out on — beside what they now credit. On these accounts the first exceeds the '
                + 'second, and Adwin\'s invoices match the first, in several cases to the cent. The '
                + 'higher figure stands here, because a report Adwin invoiced against cannot be '
                + 'reduced by an assertion. Their own annotations describe the reductions as '
                + 'duplicated entries, over-reported CPAs, a bonus that did not trigger, an operator '
                + 'dispute still open and, on 10Bet, an account they could not log in to.'}
            />
            <FactCard
              title="Invoice 20318 as issued"
              rows={[
                ['Gross identified underpayment', SETTLEMENT_FACTS.invoice20318Gross],
                ['Credits applied', -SETTLEMENT_FACTS.invoice20318Credits],
                ['Net', SETTLEMENT_FACTS.invoice20318Gross - SETTLEMENT_FACTS.invoice20318Credits],
                ['This report, before entitlements', outstanding.commissionClaim],
              ]}
              note={'The 16 credit rows were invoice 20023 in full, the five fee lines of Mar-Jun 2025 '
                + 'and the ten reconciliation-fee lines: everything already received against this '
                + 'claim, which is why the statement above deducts the same three items.'}
            />
          </div>

          {tieOut && (
            <div className="bg-[hsl(225,15%,10%)] rounded-xl border border-[hsl(225,15%,18%)] overflow-hidden">
              <div className="px-4 py-3 border-b border-[hsl(225,15%,15%)]">
                <p className="text-xs text-zinc-200">What Raketech reported against what Adwin billed, month by month, on Raketech's own ledger</p>
                <p className="text-[10px] text-zinc-500 mt-1 leading-relaxed max-w-4xl">
                  Raketech's July 2026 file carries its Finance ledger of Adwin's invoices, each labelled with the period it bills.
                  "Reported" is the monthly-report detail the claim deducts — what Adwin invoiced on. "Billed" is what Adwin actually
                  invoiced for the month on that ledger, net of fee lines. Where reported exceeds billed, commission Raketech said was
                  earned was never billed in full; where billed exceeds reported, an earlier shortfall was being caught up. The cumulative
                  column shows the net position over time: it settles at {fmtEURExact(tieOut.neverBilled)} and stays there from October 2024,
                  which places the whole difference in 2023 and the first three quarters of 2024.
                  {tieOut.placed.length > 0 && (
                    <> Placed by hand: {tieOut.placed.map((p) => `${p.invoice} → ${fmtMonth(p.month)} (${p.why})`).join('; ')}.</>
                  )}
                </p>
              </div>
              <div className="overflow-x-auto">
                <table className="w-full text-[11px]">
                  <thead className="bg-[hsl(225,15%,12%)] text-zinc-500">
                    <tr>
                      <th className="px-3 py-1.5 text-left font-medium">Month</th>
                      <th className="px-3 py-1.5 text-right font-medium">Reported (claim deducts)</th>
                      <th className="px-3 py-1.5 text-right font-medium">Billed (their ledger)</th>
                      <th className="px-3 py-1.5 text-right font-medium">Difference</th>
                      <th className="px-3 py-1.5 text-right font-medium">Cumulative</th>
                      <th className="px-3 py-1.5 text-left font-medium">Invoices</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-[hsl(225,15%,14%)]">
                    {tieOut.months.map((m) => (
                      <tr key={m.month} className={`${Math.abs(m.difference) > Math.max(2000, m.reported * 0.03) ? 'bg-amber-500/[0.04]' : ''}`}>
                        <td className="px-3 py-1 text-zinc-300">{fmtMonth(m.month)}</td>
                        <td className="px-3 py-1 text-right tabular-nums text-zinc-300">{fmtEURExact(m.reported)}</td>
                        <td className="px-3 py-1 text-right tabular-nums text-zinc-400">{fmtEURExact(m.billed)}</td>
                        <td className={`px-3 py-1 text-right tabular-nums ${m.difference > 1000 ? 'text-red-400' : m.difference < -1000 ? 'text-emerald-400' : 'text-zinc-500'}`}>{fmtEURExact(m.difference)}</td>
                        <td className="px-3 py-1 text-right tabular-nums text-zinc-300">{fmtEURExact(m.cumulative)}</td>
                        <td className="px-3 py-1 text-zinc-600">{m.invoices.join(' ')}</td>
                      </tr>
                    ))}
                  </tbody>
                  <tfoot className="bg-[hsl(225,15%,12%)] text-zinc-200 font-medium">
                    <tr>
                      <td className="px-3 py-1.5">Total</td>
                      <td className="px-3 py-1.5 text-right tabular-nums">{fmtEURExact(tieOut.totalReported)}</td>
                      <td className="px-3 py-1.5 text-right tabular-nums">{fmtEURExact(tieOut.totalBilled)}</td>
                      <td className="px-3 py-1.5 text-right tabular-nums text-red-400">{fmtEURExact(tieOut.neverBilled)}</td>
                      <td />
                      <td />
                    </tr>
                  </tfoot>
                </table>
              </div>
            </div>
          )}
        </div>
      )}

      {/* ------------------------------------------------------------------ */}
      {/* Coverage gaps */}
      {/* ------------------------------------------------------------------ */}
      {activeTab === 'coverage' && (
        <GapsTab rows={claim.rows} summary={summary} lastMonth={lastMonth} />
      )}

      {/* ------------------------------------------------------------------ */}
      {/* Bridge */}
      {/* ------------------------------------------------------------------ */}
      {activeTab === 'bridge' && (
        <div className="space-y-4 animate-fade-in">
          <Explainer>
            Their report concludes they owe {fmtEURExact(theirClaim)}. This report concludes
            {' '}{fmtEURExact(Math.abs(claimTotals.underpaid))}. The gap is not a disagreement about what was
            earned — it comes from how the two documents are built. Theirs nets one grand total against
            another, so accounts they overpaid silently cancel accounts they underpaid, and it then
            deducts transaction fees their own ledger does not support. This one measures each account
            separately and adds nothing back.
          </Explainer>

          <div className="bg-[hsl(225,15%,10%)] rounded-xl border border-[hsl(225,15%,18%)] overflow-hidden divide-y divide-[hsl(225,15%,14%)]">
            {bridge.map((step, i) => <BridgeRow key={i} step={step} />)}
          </div>
        </div>
      )}

      {/* ------------------------------------------------------------------ */}
      {/* Evidence used */}
      {/* ------------------------------------------------------------------ */}
      {activeTab === 'sources' && (
        <div className="space-y-4 animate-fade-in">
          <Explainer>
            Which source won for each account. The hierarchy is deliberately "highest wins": we take
            the strongest evidence of what was actually earned, whether that is ours or theirs. Rows
            decided by their own restatement are the most defensible, because disputing them means
            disputing their own report.
          </Explainer>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
            {(Object.keys(CLAIM_SOURCE_LABELS) as ClaimSource[]).map((source) => {
              const b = summary.bySource[source];
              return (
                <div key={source} className="bg-[hsl(225,15%,10%)] rounded-xl border border-[hsl(225,15%,18%)] p-4">
                  <span className={`inline-flex px-1.5 py-0.5 rounded border text-[10px] font-medium mb-3 ${SOURCE_STYLES[source]}`}>
                    {SOURCE_SHORT[source]}
                  </span>
                  <p className="text-[11px] text-zinc-500 mb-3 leading-relaxed">{CLAIM_SOURCE_LABELS[source]}</p>
                  <dl className="space-y-1.5 text-xs">
                    <div className="flex justify-between">
                      <dt className="text-zinc-500">Accounts</dt>
                      <dd className="text-zinc-200 tabular-nums">{b.accounts}</dd>
                    </div>
                    <div className="flex justify-between">
                      <dt className="text-zinc-500">Earned</dt>
                      <dd className="text-emerald-400 tabular-nums">{fmtEUR(b.earned)}</dd>
                    </div>
                    <div className="flex justify-between">
                      <dt className="text-zinc-500">Net position</dt>
                      <dd className={`tabular-nums ${b.underpaid < 0 ? 'text-red-400' : 'text-zinc-400'}`}>
                        {fmtEUR(b.underpaid)}
                      </dd>
                    </div>
                  </dl>
                </div>
              );
            })}
          </div>

          <div className="bg-[hsl(225,15%,10%)] rounded-xl border border-[hsl(225,15%,18%)] p-4">
            <h3 className="text-sm font-semibold text-white mb-3">What their restatement changes</h3>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 text-xs">
              <div>
                <p className="text-zinc-500 mb-1">Claim without their new figures</p>
                <p className="text-lg text-zinc-300 tabular-nums">{fmtEUR(Math.abs(summary.underpaidWithoutRestatement))}</p>
                <p className="text-[10px] text-zinc-600 mt-1">v1's evidence only, same period</p>
              </div>
              <div>
                <p className="text-zinc-500 mb-1">Claim with them</p>
                <p className="text-lg text-red-400 tabular-nums font-semibold">{fmtEUR(Math.abs(summary.underpaidTotal))}</p>
                <p className="text-[10px] text-zinc-600 mt-1">All four sources, highest wins</p>
              </div>
              <div>
                <p className="text-zinc-500 mb-1">Added by their own admission</p>
                <p className="text-lg text-amber-400 tabular-nums font-semibold">{fmtEUR(summary.restatementEffect)}</p>
                <p className="text-[10px] text-zinc-600 mt-1">
                  {summary.accountsNewToReport} accounts appear only in their own workbooks
                </p>
              </div>
            </div>
          </div>

          <div className="bg-[hsl(225,15%,10%)] rounded-xl border border-[hsl(225,15%,18%)] p-4">
            <h3 className="text-sm font-semibold text-white mb-1">What dropping the January 2025 column changes</h3>
            <p className="text-[11px] text-zinc-500 mb-3 leading-relaxed">
              Raketech's June 2025 workbook carries the January file as "New earnings 1st file (EUR)"
              beside the column it credits Adwin on. adwstats had imported the first of those. Every
              account below was worth more on the disowned column than on anything Raketech now stands
              behind, so v3 gives it up rather than defend it.
            </p>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 text-xs mb-4">
              <div>
                <p className="text-zinc-500 mb-1">v2, on the January column</p>
                <p className="text-lg text-zinc-300 tabular-nums">{fmtEUR(Math.abs(summary.underpaidOnJanuaryColumn))}</p>
              </div>
              <div>
                <p className="text-zinc-500 mb-1">v3, on their June column</p>
                <p className="text-lg text-red-400 tabular-nums font-semibold">{fmtEUR(Math.abs(summary.underpaidTotal))}</p>
              </div>
              <div>
                <p className="text-zinc-500 mb-1">Given up</p>
                <p className="text-lg text-sky-400 tabular-nums font-semibold">{fmtEUR(summary.januaryColumnEffect)}</p>
              </div>
            </div>
            <div className="overflow-x-auto border border-[hsl(225,15%,18%)] rounded-lg">
              <table className="w-full text-[11px]">
                <thead className="bg-[hsl(225,15%,8%)]">
                  <tr>
                    <th className="text-left px-3 py-1.5 text-zinc-500 font-medium">Account</th>
                    <th className="text-right px-3 py-1.5 text-zinc-500 font-medium">January file</th>
                    <th className="text-right px-3 py-1.5 text-zinc-500 font-medium">June credited</th>
                    <th className="text-right px-3 py-1.5 text-zinc-500 font-medium">Their July 2026</th>
                    <th className="text-right px-3 py-1.5 text-sky-400/70 font-medium">Given up</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-[hsl(225,15%,14%)]">
                  {claim.rows
                    .filter((r) => r.januaryColumnEffect > 1)
                    .sort((a, b) => b.januaryColumnEffect - a.januaryColumnEffect)
                    .slice(0, 40)
                    .map((r) => (
                      <tr key={r.key} className="hover:bg-[hsl(225,15%,12%)]">
                        <td className="px-3 py-1.5">
                          <span className="text-zinc-300">{r.brands[0] || r.partner || '—'}</span>
                          <span className="text-zinc-600"> · {r.accountId}</span>
                        </td>
                        <td className="px-3 py-1.5 text-right tabular-nums text-zinc-500">{fmtEUR(r.reference.previouslyClaimed)}</td>
                        <td className="px-3 py-1.5 text-right tabular-nums text-sky-300">
                          {fmtEUR(r.candidates.find((c) => c.source === 'june_recon')?.amount || 0)}
                        </td>
                        <td className="px-3 py-1.5 text-right tabular-nums text-amber-400/80">
                          {fmtEUR(r.candidates.find((c) => c.source === 'raketech_restated')?.amount || 0)}
                        </td>
                        <td className="px-3 py-1.5 text-right tabular-nums text-sky-400">{fmtEUR(r.januaryColumnEffect)}</td>
                      </tr>
                    ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}

      {/* ------------------------------------------------------------------ */}
      {/* Their June file */}
      {/* ------------------------------------------------------------------ */}
      {activeTab === 'june' && (
        <div className="space-y-4 animate-fade-in">
          <Explainer>
            The June 2025 workbook on its own terms, before any of our figures touch it. It carries
            three earnings columns for the same accounts — what Raketech credits Adwin, what their
            monthly reports actually paid out on, and the January file — and its own Comments column
            annotates where the reporting went wrong. Every figure here is theirs.
          </Explainer>

          <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
            <StatCard
              label="They credit Adwin"
              value={fmtEUR(june.controls.totals.adwinEarnings)}
              hint={`${june.controls.accounts} accounts, ${june.months[0]} to ${june.months[june.months.length - 1]}`}
              icon={<Scale className="h-4 w-4" />}
              color="zinc"
            />
            <StatCard
              label="They under-reported"
              value={fmtEUR(june.controls.underReported.amount)}
              hint={`${june.controls.underReported.rows} rows, in Adwin's favour`}
              icon={<ArrowDownRight className="h-4 w-4" />}
              color="red"
            />
            <StatCard
              label="They over-reported"
              value={fmtEUR(june.controls.overReported.amount)}
              hint={`${june.controls.overReported.rows} rows, against Adwin`}
              icon={<AlertTriangle className="h-4 w-4" />}
              color="amber"
            />
            <StatCard
              label="Returned the reported figure"
              value={fmtEUR(june.controls.circular.amount)}
              hint={`${june.controls.circular.rows} rows reconciled to themselves`}
              icon={<Shield className="h-4 w-4" />}
              color="purple"
            />
          </div>

          <div className="bg-[hsl(225,15%,10%)] rounded-xl border border-[hsl(225,15%,18%)] p-4">
            <h3 className="text-sm font-semibold text-white mb-1">Their own closing block, as it stands in the file</h3>
            <p className="text-[11px] text-zinc-500 mb-3 leading-relaxed">
              The working beside their Summary pivot, transcribed line for line. Note that
              "Total transaction fees" carries two different amounts, and that the second is 95% of
              the first — the split they applied without it being agreed.
            </p>
            <div className="divide-y divide-[hsl(225,15%,14%)] border border-[hsl(225,15%,18%)] rounded-lg">
              {june.summary.headline.map((line, i) => (
                <div key={`${line.label}|${i}`} className="flex items-center justify-between gap-4 px-3 py-1.5">
                  <span className="text-[11px] text-zinc-400">{line.label}</span>
                  <span className={`text-[11px] tabular-nums ${line.amount < 0 ? 'text-red-400' : 'text-zinc-300'}`}>
                    {fmtEURExact(line.amount)}
                  </span>
                </div>
              ))}
            </div>
          </div>

          <div className="bg-[hsl(225,15%,10%)] rounded-xl border border-[hsl(225,15%,18%)] p-4">
            <h3 className="text-sm font-semibold text-white mb-1">Where their own file records a reporting failure</h3>
            <p className="text-[11px] text-zinc-500 mb-3 leading-relaxed">
              Rows their Comments column annotates as an error on their side —
              {' '}{june.controls.admissions.rows} of them, covering
              {' '}{fmtEUR(june.controls.admissions.amount)} of credited earnings. Sorted by how far the
              account was under-reported.
            </p>
            <div className="overflow-x-auto border border-[hsl(225,15%,18%)] rounded-lg">
              <table className="w-full text-[11px]">
                <thead className="bg-[hsl(225,15%,8%)]">
                  <tr>
                    <th className="text-left px-3 py-1.5 text-zinc-500 font-medium">Account</th>
                    <th className="text-right px-3 py-1.5 text-zinc-500 font-medium">Credited</th>
                    <th className="text-right px-3 py-1.5 text-zinc-500 font-medium">Reported</th>
                    <th className="text-right px-3 py-1.5 text-red-400/70 font-medium">Difference</th>
                    <th className="text-left px-3 py-1.5 text-zinc-500 font-medium">Their comment</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-[hsl(225,15%,14%)]">
                  {[...june.accounts]
                    .filter((a) => a.comments.length > 0 || a.difference < -500)
                    .sort((a, b) => a.difference - b.difference)
                    .slice(0, 60)
                    .map((a) => (
                      <tr key={a.id} className="hover:bg-[hsl(225,15%,12%)] align-top">
                        <td className="px-3 py-1.5">
                          <span className="text-zinc-300">{a.brands[0] || a.partners[0] || '—'}</span>
                          <span className="text-zinc-600"> · {a.id}</span>
                        </td>
                        <td className="px-3 py-1.5 text-right tabular-nums text-sky-300">{fmtEUR(a.adwinEarnings)}</td>
                        <td className="px-3 py-1.5 text-right tabular-nums text-zinc-400">{fmtEUR(a.reported)}</td>
                        <td className={`px-3 py-1.5 text-right tabular-nums ${
                          a.difference < -1 ? 'text-red-400' : a.difference > 1 ? 'text-amber-400' : 'text-zinc-600'
                        }`}>
                          {Math.abs(a.difference) > 1 ? fmtEUR(a.difference) : '—'}
                        </td>
                        <td className="px-3 py-1.5 text-zinc-500 max-w-[420px]">{a.comments.join(' · ') || '—'}</td>
                      </tr>
                    ))}
                </tbody>
              </table>
            </div>
          </div>

          <div className="bg-[hsl(225,15%,10%)] rounded-xl border border-[hsl(225,15%,18%)] p-4">
            <h3 className="text-sm font-semibold text-white mb-1">Adjustments with no account attached</h3>
            <p className="text-[11px] text-zinc-500 mb-3 leading-relaxed">
              {june.controls.unidentifiedRows} rows of the workbook carry no account id at all, only the
              username "Corrections". They net to {fmtEURExact(june.controls.unidentified.reported)}, of which
              {' '}{fmtEURExact(june.controls.unidentified.negative)} is deducted from Adwin without an account
              to check it against.
            </p>
            <div className="overflow-x-auto border border-[hsl(225,15%,18%)] rounded-lg">
              <table className="w-full text-[11px]">
                <thead className="bg-[hsl(225,15%,8%)]">
                  <tr>
                    <th className="text-left px-3 py-1.5 text-zinc-500 font-medium">Partner</th>
                    <th className="text-left px-3 py-1.5 text-zinc-500 font-medium">Brand</th>
                    <th className="text-right px-3 py-1.5 text-zinc-500 font-medium">Reported</th>
                    <th className="text-right px-3 py-1.5 text-zinc-500 font-medium">Credited</th>
                    <th className="text-left px-3 py-1.5 text-zinc-500 font-medium">Their comment</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-[hsl(225,15%,14%)]">
                  {[...june.unidentified]
                    .sort((a, b) => a.reported - b.reported)
                    .slice(0, 60)
                    .map((r, i) => (
                      <tr key={`${r.partner}|${r.brand}|${i}`} className="hover:bg-[hsl(225,15%,12%)]">
                        <td className="px-3 py-1.5 text-zinc-300">{r.partner || '—'}</td>
                        <td className="px-3 py-1.5 text-zinc-400">{r.brand || '—'}</td>
                        <td className={`px-3 py-1.5 text-right tabular-nums ${r.reported < 0 ? 'text-red-400' : 'text-zinc-400'}`}>
                          {fmtEUR(r.reported)}
                        </td>
                        <td className="px-3 py-1.5 text-right tabular-nums text-zinc-500">{fmtEUR(r.adwinEarnings)}</td>
                        <td className="px-3 py-1.5 text-zinc-500 max-w-[380px]">{r.comment || '—'}</td>
                      </tr>
                    ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}

      {/* ------------------------------------------------------------------ */}
      {/* Unverifiable */}
      {/* ------------------------------------------------------------------ */}
      {activeTab === 'unverifiable' && (
        <UnverifiableTab rows={claim.rows} access={accessMap} lastMonth={lastMonth} />
      )}

      {/* ------------------------------------------------------------------ */}
      {/* Access */}
      {/* ------------------------------------------------------------------ */}
      {activeTab === 'access' && (
        <AccessTab rows={claim.rows} access={accessMap} raw={access} lastMonth={lastMonth} />
      )}

      {/* ------------------------------------------------------------------ */}
      {/* PA = NO with players; Write-downs — built to be sent */}
      {/* ------------------------------------------------------------------ */}
      {activeTab === 'pano' && (
        <PaNoTab rows={claim.rows} access={accessMap} lastMonth={lastMonth} onShare={shareFromTab} />
      )}
      {activeTab === 'writedowns' && (
        <WriteDownsTab rows={claim.rows} access={accessMap} lastMonth={lastMonth} onShare={shareFromTab} />
      )}

      {showShare && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm p-4"
          onClick={() => !isSaving && closeShare()}
        >
          <div
            className="w-full max-w-lg bg-[hsl(225,15%,10%)] border border-[hsl(225,15%,20%)] rounded-xl p-6"
            onClick={(e) => e.stopPropagation()}
          >
            {!savedUrl ? (
              <>
                <div className="flex items-start justify-between mb-4">
                  <div>
                    <h3 className="text-sm font-semibold text-white">
                      {shareSelection ? 'Save & share this selection' : 'Save & share this report'}
                    </h3>
                    <p className="text-[11px] text-zinc-500 mt-1">
                      {shareSelection
                        ? `Freezes the ${shareSelection.rows.length} accounts selected on the ${shareSelection.focus.kind === 'pa_no_players' ? 'PA = NO' : 'Write-downs'} tab, with their months, at a link anyone can open. The selection and its purpose are stated on the page.`
                        : `Freezes all ${claim.rows.length} accounts as they stand through ${fmtMonth(lastMonth)}, month by month, at a link anyone can open. Later changes to the report leave it untouched.`}
                    </p>
                  </div>
                  <button onClick={closeShare} className="text-zinc-500 hover:text-zinc-300">
                    <X className="h-4 w-4" />
                  </button>
                </div>

                <div className="space-y-3">
                  <div>
                    <label className="block text-[11px] text-zinc-500 mb-1">Title</label>
                    <input
                      value={shareTitle}
                      onChange={(e) => setShareTitle(e.target.value)}
                      className="w-full px-3 py-2 text-xs bg-[hsl(225,15%,12%)] border border-[hsl(225,15%,20%)] rounded-lg text-zinc-200 outline-none focus:border-amber-500/40"
                    />
                  </div>
                  <div>
                    <label className="block text-[11px] text-zinc-500 mb-1">
                      Description, shown at the top of the shared page
                    </label>
                    <textarea
                      value={shareDesc}
                      onChange={(e) => setShareDesc(e.target.value)}
                      rows={3}
                      placeholder="Prepared for Raketech, on the Adwin earnings column of the June 2025 reconciliation."
                      className="w-full px-3 py-2 text-xs bg-[hsl(225,15%,12%)] border border-[hsl(225,15%,20%)] rounded-lg text-zinc-200 outline-none focus:border-amber-500/40 resize-none"
                    />
                  </div>
                  <div>
                    <label className="block text-[11px] text-zinc-500 mb-1">
                      Password, optional
                    </label>
                    <input
                      type="text"
                      value={sharePassword}
                      onChange={(e) => setSharePassword(e.target.value)}
                      placeholder="Leave empty for a link that opens without one"
                      className="w-full px-3 py-2 text-xs bg-[hsl(225,15%,12%)] border border-[hsl(225,15%,20%)] rounded-lg text-zinc-200 outline-none focus:border-amber-500/40"
                    />
                  </div>
                </div>

                <div className="flex items-center justify-end gap-2 mt-5">
                  <button onClick={closeShare} className="px-3 py-2 text-xs text-zinc-400 hover:text-zinc-200">
                    Cancel
                  </button>
                  <button
                    onClick={saveSnapshot}
                    disabled={isSaving || !shareTitle.trim()}
                    className="flex items-center gap-1.5 px-4 py-2 text-xs font-medium text-amber-950 bg-amber-500 hover:bg-amber-400 rounded-lg disabled:opacity-50"
                  >
                    {isSaving ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Share2 className="h-3.5 w-3.5" />}
                    {isSaving ? 'Saving…' : 'Create link'}
                  </button>
                </div>
              </>
            ) : (
              <>
                <div className="flex items-start justify-between mb-4">
                  <h3 className="text-sm font-semibold text-white">Link ready</h3>
                  <button onClick={closeShare} className="text-zinc-500 hover:text-zinc-300">
                    <X className="h-4 w-4" />
                  </button>
                </div>
                <div className="flex items-center gap-2 p-3 bg-[hsl(225,15%,12%)] border border-[hsl(225,15%,20%)] rounded-lg">
                  <LinkIcon className="h-3.5 w-3.5 text-zinc-500 shrink-0" />
                  <span className="text-xs text-zinc-300 truncate flex-1">{savedUrl}</span>
                  <button
                    onClick={() => {
                      navigator.clipboard.writeText(savedUrl);
                      setCopied(true);
                      setTimeout(() => setCopied(false), 2000);
                    }}
                    className="px-2 py-1 text-[11px] text-amber-400 hover:text-amber-300 shrink-0"
                  >
                    {copied ? <Check className="h-3.5 w-3.5" /> : 'Copy'}
                  </button>
                </div>
                {sharePassword && (
                  <p className="text-[11px] text-zinc-500 mt-2 flex items-center gap-1.5">
                    <Lock className="h-3 w-3" /> Password protected. Send the password separately.
                  </p>
                )}
              </>
            )}

            {snapshots.length > 0 && (
              <div className="mt-5 pt-4 border-t border-[hsl(225,15%,18%)]">
                <p className="text-[11px] text-zinc-500 mb-2">Saved reports</p>
                <div className="space-y-1 max-h-44 overflow-y-auto">
                  {snapshots.map((s) => (
                    <div key={s.id} className="flex items-center gap-2 text-[11px] text-zinc-400 py-1">
                      <a
                        href={s.url}
                        target="_blank"
                        rel="noreferrer"
                        className="truncate flex-1 hover:text-amber-400"
                      >
                        {s.title}
                        {s.hasPassword && <Lock className="h-2.5 w-2.5 inline ml-1" />}
                      </a>
                      <span className="text-zinc-600 shrink-0">
                        {new Date(s.createdAt).toLocaleDateString('en-GB')} · {s.viewCount} views
                      </span>
                      <button
                        onClick={() => deleteSnapshot(s.id)}
                        className="text-zinc-600 hover:text-red-400 shrink-0"
                      >
                        <Trash2 className="h-3 w-3" />
                      </button>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

// ---------------------------------------------------------------------------
// Pieces
// ---------------------------------------------------------------------------
const CARD_COLORS: Record<string, string> = {
  red: 'text-red-400 bg-red-500/10 border-red-500/20',
  amber: 'text-amber-400 bg-amber-500/10 border-amber-500/20',
  emerald: 'text-emerald-400 bg-emerald-500/10 border-emerald-500/20',
  cyan: 'text-cyan-400 bg-cyan-500/10 border-cyan-500/20',
  purple: 'text-purple-400 bg-purple-500/10 border-purple-500/20',
  zinc: 'text-zinc-300 bg-zinc-500/10 border-zinc-500/20',
};

const StatCard: React.FC<{
  label: string;
  value: string;
  hint?: string;
  icon: React.ReactNode;
  color: keyof typeof CARD_COLORS;
}> = ({ label, value, hint, icon, color }) => (
  <div className="bg-[hsl(225,15%,10%)] rounded-xl border border-[hsl(225,15%,18%)] p-4">
    <div className="flex items-center justify-between mb-2">
      <span className="text-[11px] text-zinc-500 uppercase tracking-wider">{label}</span>
      <span className={`p-1.5 rounded-lg border ${CARD_COLORS[color]}`}>{icon}</span>
    </div>
    <p className="text-lg font-semibold text-white tabular-nums">{value}</p>
    {hint && <p className="text-[10px] text-zinc-500 mt-0.5">{hint}</p>}
  </div>
);

const Explainer: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <div className="bg-[hsl(225,15%,9%)] border border-[hsl(225,15%,16%)] rounded-lg px-4 py-3">
    <p className="text-xs text-zinc-400 leading-relaxed">{children}</p>
  </div>
);

const OutstandingRow: React.FC<{ line: OutstandingLine }> = ({ line }) => {
  const tone: Record<OutstandingLine['kind'], string> = {
    claim: 'text-zinc-200',
    credit: 'text-red-400',
    entitlement: 'text-emerald-400',
    invoice: 'text-emerald-400',
    subtotal: 'text-white font-semibold',
    total: 'text-amber-400 font-bold',
  };
  const emphasised = line.kind === 'subtotal' || line.kind === 'total';
  return (
    <div className={`px-4 py-3 ${emphasised ? 'bg-[hsl(225,15%,12%)]' : ''}`}>
      <div className="flex items-start justify-between gap-4">
        <div className="min-w-0">
          <div className="flex items-center gap-2">
            <p className={`text-xs ${emphasised ? 'text-white font-medium' : 'text-zinc-300'}`}>
              {line.label}
            </p>
            {line.provisional && (
              <span className="px-1.5 py-0.5 rounded border border-amber-500/20 bg-amber-500/10 text-amber-400 text-[9px] uppercase tracking-wide">
                to be reclaimed
              </span>
            )}
          </div>
          {line.note && <p className="text-[11px] text-zinc-500 mt-1 leading-relaxed">{line.note}</p>}
        </div>
        <p className={`text-sm tabular-nums shrink-0 ${tone[line.kind]}`}>{fmtEURExact(line.amount)}</p>
      </div>
    </div>
  );
};

const FactCard: React.FC<{
  title: string;
  rows: [string, number][];
  note: string;
}> = ({ title, rows, note }) => (
  <div className="bg-[hsl(225,15%,10%)] rounded-xl border border-[hsl(225,15%,18%)] p-4">
    <p className="text-xs text-zinc-300 font-medium mb-2.5">{title}</p>
    <div className="space-y-1.5">
      {rows.map(([label, amount]) => (
        <div key={label} className="flex items-baseline justify-between gap-3">
          <span className="text-[11px] text-zinc-500">{label}</span>
          <span className={`text-xs tabular-nums ${amount < 0 ? 'text-red-400' : 'text-zinc-200'}`}>
            {fmtEURExact(amount)}
          </span>
        </div>
      ))}
    </div>
    <p className="text-[11px] text-zinc-500 mt-3 leading-relaxed">{note}</p>
  </div>
);

/** One of the two bases the shortfall can be stated on, selectable. */
const BandCard: React.FC<{
  active: boolean;
  onClick: () => void;
  title: string;
  amount: number;
  body: string;
}> = ({ active, onClick, title, amount, body }) => (
  <button
    type="button"
    onClick={onClick}
    className={`text-left rounded-xl border px-4 py-3 transition-colors ${active
      ? 'border-emerald-500/40 bg-emerald-500/[0.06]'
      : 'border-[hsl(225,15%,18%)] bg-[hsl(225,15%,10%)] hover:border-[hsl(225,15%,26%)]'}`}
  >
    <div className={`text-[11px] uppercase tracking-wide ${active ? 'text-emerald-400/90' : 'text-zinc-500'}`}>
      {title}
    </div>
    <div className={`text-xl font-semibold tabular-nums mt-1 ${active ? 'text-emerald-300' : 'text-zinc-200'}`}>
      {fmtEURExact(amount)}
    </div>
    <p className="text-[11px] text-zinc-500 leading-relaxed mt-1.5">{body}</p>
  </button>
);

const BridgeRow: React.FC<{ step: ClaimBridgeStep }> = ({ step }) => {
  const tone: Record<ClaimBridgeStep['kind'], string> = {
    figure: 'text-zinc-200',
    subtotal: 'text-white font-semibold',
    addition: 'text-emerald-400',
    deduction: 'text-red-400',
    result: 'text-red-400 font-bold',
  };
  return (
    <div className={`px-4 py-3 ${step.kind === 'subtotal' || step.kind === 'result' ? 'bg-[hsl(225,15%,12%)]' : ''}`}>
      <div className="flex items-start justify-between gap-4">
        <div className="min-w-0">
          <div className="flex items-center gap-2">
            <p className={`text-xs ${step.kind === 'result' || step.kind === 'subtotal' ? 'text-white font-medium' : 'text-zinc-300'}`}>
              {step.label}
            </p>
            {step.theirFigure && (
              <span className="px-1.5 py-0.5 rounded border border-zinc-500/20 bg-zinc-500/10 text-zinc-400 text-[9px] uppercase tracking-wide">
                their figure
              </span>
            )}
          </div>
          {step.note && <p className="text-[11px] text-zinc-500 mt-1 leading-relaxed">{step.note}</p>}
        </div>
        <div className="text-right shrink-0">
          <p className={`text-sm tabular-nums ${tone[step.kind]}`}>
            {step.amount === null ? '—' : fmtEURExact(step.amount)}
          </p>
        </div>
      </div>
    </div>
  );
};

/**
 * Month-by-month view of every figure held for an account.
 *
 * Synced and Affiliation Cloud are shown for comparison even when they lost to
 * another source, and the January column is shown although it can never win, so
 * the drilldown is the complete evidence picture. Only `Best` feeds the claim;
 * a dot marks which column supplied it.
 */
const MonthlyComparison: React.FC<{ row: ClaimRow }> = ({ row }) => {
  const months = row.monthly;
  if (months.length === 0) return null;

  const has = {
    ftds: months.some((m) => m.ftds > 0),
    invoiced: months.some((m) => m.invoiced !== 0),
    juneRecon: months.some((m) => m.juneRecon !== 0),
    januaryFile: months.some((m) => m.januaryFile !== 0),
    synced: months.some((m) => m.synced !== 0),
    ac: months.some((m) => m.ac !== 0),
    restated: months.some((m) => m.restated !== 0),
  };

  const cell = (value: number, isBest: boolean, tone: string) => (
    <td className={`py-1 px-2 text-right tabular-nums ${value ? tone : 'text-zinc-700'}`}>
      {value ? fmtEUR(value) : '—'}
      {isBest && value > 0 && <span className="text-white/70"> •</span>}
    </td>
  );

  const totals = months.reduce((acc, m) => ({
    invoiced: acc.invoiced + m.invoiced,
    juneRecon: acc.juneRecon + m.juneRecon,
    januaryFile: acc.januaryFile + m.januaryFile,
    synced: acc.synced + m.synced,
    ac: acc.ac + m.ac,
    restated: acc.restated + m.restated,
    earned: acc.earned + m.earned,
    ftds: acc.ftds + m.ftds,
  }), { invoiced: 0, juneRecon: 0, januaryFile: 0, synced: 0, ac: 0, restated: 0, earned: 0, ftds: 0 });

  return (
    <div>
      <div className="flex flex-wrap items-baseline justify-between gap-2 mb-2">
        <p className="text-[11px] text-zinc-500">
          Month by month, every figure available. Only <span className="text-white/80">Best</span> is claimed on;
          the others are shown for comparison.
          {row.chosenSource === 'june_reported' && (
            <span className="text-amber-400/80">
              {' '}This account is claimed on their reported column, which their file gives as one total
              with no months behind it. Best therefore carries their credited shape scaled up to that
              total: the account figure is theirs exactly, the split between months is inferred.
            </span>
          )}
        </p>
        <p className="text-[10px] text-zinc-600">• marks the source used that month</p>
      </div>
      <div className="overflow-x-auto border border-[hsl(225,15%,18%)] rounded-lg">
        <table className="w-full text-[10px]">
          <thead className="bg-[hsl(225,15%,10%)]">
            <tr>
              <th className="text-left py-1.5 px-2 text-zinc-500 font-medium">Month</th>
              {has.ftds && <th className="text-right py-1.5 px-2 text-zinc-500 font-medium">FTDs</th>}
              {has.invoiced && <th className="text-right py-1.5 px-2 text-zinc-400 font-medium">Invoiced</th>}
              {has.juneRecon && <th className="text-right py-1.5 px-2 text-sky-400/70 font-medium">June recon</th>}
              {has.januaryFile && (
                <th
                  className="text-right py-1.5 px-2 text-zinc-600 font-medium"
                  title="The January 2025 file Raketech disowned. Shown for reference; never claimed on."
                >
                  Jan file
                </th>
              )}
              {has.synced && <th className="text-right py-1.5 px-2 text-emerald-400/70 font-medium">Synced −5%</th>}
              {has.ac && <th className="text-right py-1.5 px-2 text-indigo-400/70 font-medium">AC</th>}
              {has.restated && <th className="text-right py-1.5 px-2 text-amber-400/70 font-medium">Restated</th>}
              <th className="text-right py-1.5 px-2 text-white/70 font-medium">Best</th>
              <th className="text-right py-1.5 px-2 text-red-400/70 font-medium">Diff</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-[hsl(225,15%,13%)]">
            {months.map((m) => (
              <tr key={m.month} className={m.unsupported ? 'bg-amber-500/[0.06]' : undefined}>
                <td className="py-1 px-2 text-zinc-400 whitespace-nowrap">{fmtMonth(m.month)}</td>
                {has.ftds && (
                  <td className="py-1 px-2 text-right tabular-nums text-zinc-500">
                    {m.ftds ? fmtInt(m.ftds) : '—'}
                  </td>
                )}
                {has.invoiced && cell(m.invoiced, false, 'text-zinc-300')}
                {has.juneRecon && cell(m.juneRecon, m.juneRecon === m.earned, 'text-sky-300')}
                {has.januaryFile && cell(m.januaryFile, false, 'text-zinc-600')}
                {has.synced && cell(m.synced, m.synced === m.earned, 'text-emerald-400')}
                {has.ac && cell(m.ac, m.ac === m.earned, 'text-indigo-400')}
                {has.restated && cell(m.restated, m.restated === m.earned, 'text-amber-400')}
                <td className="py-1 px-2 text-right tabular-nums text-white">
                  {m.earned ? fmtEUR(m.earned) : '—'}
                </td>
                <td className={`py-1 px-2 text-right tabular-nums ${
                  m.unsupported ? 'text-amber-400'
                    : m.underpaid < -1 ? 'text-red-400'
                      : m.underpaid > 1 ? 'text-amber-400/80' : 'text-zinc-700'
                }`}>
                  {m.unsupported
                    ? <span title="Invoiced with nothing to compare against">no data</span>
                    : Math.abs(m.underpaid) > 1 ? fmtEUR(m.underpaid) : '—'}
                </td>
              </tr>
            ))}
          </tbody>
          <tfoot className="bg-[hsl(225,15%,10%)] border-t border-[hsl(225,15%,18%)]">
            <tr className="font-medium">
              <td className="py-1.5 px-2 text-zinc-500">Total</td>
              {has.ftds && (
                <td className="py-1.5 px-2 text-right tabular-nums text-zinc-400">{fmtInt(totals.ftds)}</td>
              )}
              {has.invoiced && <td className="py-1.5 px-2 text-right tabular-nums text-zinc-300">{fmtEUR(totals.invoiced)}</td>}
              {has.juneRecon && <td className="py-1.5 px-2 text-right tabular-nums text-sky-300">{fmtEUR(totals.juneRecon)}</td>}
              {has.januaryFile && <td className="py-1.5 px-2 text-right tabular-nums text-zinc-600">{fmtEUR(totals.januaryFile)}</td>}
              {has.synced && <td className="py-1.5 px-2 text-right tabular-nums text-emerald-400">{fmtEUR(totals.synced)}</td>}
              {has.ac && <td className="py-1.5 px-2 text-right tabular-nums text-indigo-400">{fmtEUR(totals.ac)}</td>}
              {has.restated && <td className="py-1.5 px-2 text-right tabular-nums text-amber-400">{fmtEUR(totals.restated)}</td>}
              <td className="py-1.5 px-2 text-right tabular-nums text-white">{fmtEUR(totals.earned)}</td>
              <td className={`py-1.5 px-2 text-right tabular-nums ${
                totals.invoiced - totals.earned < 0 ? 'text-red-400' : 'text-zinc-500'
              }`}>
                {fmtEUR(totals.invoiced - totals.earned)}
              </td>
            </tr>
          </tfoot>
        </table>
      </div>
      {row.monthly.some((m) => m.unsupported) && (
        <p className="text-[10px] text-amber-500/70 mt-1.5">
          Highlighted months were invoiced with no earnings data on any source, so they cannot be judged
          either way.
        </p>
      )}
    </div>
  );
};

const ClaimDetail: React.FC<{ row: ClaimRow }> = ({ row }) => (
  <div className="space-y-4">
    <div>
      <p className="text-[11px] text-zinc-500 mb-2">
        Every earnings figure available for this account. The highest one is used.
      </p>
      <div className="flex flex-wrap gap-2">
        {row.candidates.map((c) => (
          <div
            key={c.source}
            className={`px-3 py-2 rounded-lg border text-[11px] ${
              c.source === row.chosenSource
                ? 'border-amber-500/40 bg-amber-500/5'
                : 'border-[hsl(225,15%,20%)] bg-[hsl(225,15%,11%)]'
            } ${c.available ? '' : 'opacity-40'}`}
          >
            <div className="text-zinc-500">{CLAIM_SOURCE_LABELS[c.source]}</div>
            <div className="text-zinc-100 tabular-nums">
              {c.available ? fmtEURExact(c.amount) : 'not available'}
            </div>
            {c.available && c.originalCurrency && c.originalAmount ? (
              <div className="text-[10px] text-zinc-600">
                {c.originalCurrency} {c.originalAmount.toLocaleString('en-US', { maximumFractionDigits: 0 })} original
              </div>
            ) : null}
            {c.available && c.marginApplied && (
              <div className="text-[10px] text-amber-500/70">less 5% margin</div>
            )}
          </div>
        ))}
      </div>
    </div>

    <div className="border border-[hsl(225,15%,18%)] rounded-lg p-3">
      <p className="text-[11px] text-zinc-500 mb-2">
        Reference figures. None of these can win the account; they are here so the row can be
        checked against what Raketech reported at the time and what the disowned file said.
      </p>
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 text-[11px]">
        <div>
          <div className="text-zinc-600">Their monthly reports</div>
          <div className="text-zinc-300 tabular-nums">{fmtEURExact(row.reference.raketechReported)}</div>
        </div>
        <div>
          <div className="text-zinc-600">Reported less credited</div>
          <div className={`tabular-nums ${
            row.reference.juneDifference < -1 ? 'text-red-400'
              : row.reference.juneDifference > 1 ? 'text-amber-400' : 'text-zinc-400'
          }`}>
            {fmtEURExact(row.reference.juneDifference)}
          </div>
        </div>
        <div>
          <div className="text-zinc-600">January 2025 file</div>
          <div className="text-zinc-500 tabular-nums">{fmtEURExact(row.reference.previouslyClaimed)}</div>
        </div>
        <div>
          <div className="text-zinc-600">Given up with it</div>
          <div className={`tabular-nums ${row.januaryColumnEffect > 1 ? 'text-sky-400' : 'text-zinc-600'}`}>
            {fmtEURExact(row.januaryColumnEffect)}
          </div>
        </div>
        {row.offers.length > 0 && (
          <div title="Raketech's account-level 'Adwin earnings' column takes the 5% after April 2025 as well; their own Summary sheet takes none. The claim follows the Summary.">
            <div className="text-zinc-600">Their July "Adwin earnings" column</div>
            <div className="text-zinc-300 tabular-nums">{fmtEURExact(row.reference.julyStatedAdwinEarnings || 0)}</div>
            {(() => {
              const ours = row.monthly.reduce((s, m) => s + (m.restated || 0), 0);
              const d = ours - (row.reference.julyStatedAdwinEarnings || 0);
              return d > 1 ? <div className="text-[10px] text-amber-500/70">their column takes 5% after Apr 2025: {fmtEURExact(d)}</div> : null;
            })()}
          </div>
        )}
        {row.tailWrittenDown > 50 && (
          <div title="Months after November 2024 in which Adwin invoiced, on Raketech's monthly report, more than Raketech's July 2026 report now carries.">
            <div className="text-zinc-600">July report below their monthly report, Dec 2024 on</div>
            <div className="text-red-400 tabular-nums">{fmtEURExact(row.tailWrittenDown)}</div>
          </div>
        )}
      </div>
      {row.reference.juneComments.length > 0 && (
        <p className="text-[11px] text-amber-400/80 mt-2 leading-relaxed">
          Raketech's own note: {row.reference.juneComments.join(' · ')}
        </p>
      )}
    </div>

    {(row.coverage.gapMonths > 0 || row.coverage.monthLevelLift > 1) && (
      <div className="border border-amber-500/20 bg-amber-500/[0.04] rounded-lg p-3 space-y-1.5">
        {row.coverage.gapMonths > 0 && (
          <p className="text-[11px] text-zinc-400 leading-relaxed">
            <span className="text-amber-400">
              {row.coverage.gapMonths} of this account's months carry an invoice and no reported
              earnings — {fmtEURExact(row.coverage.gapInvoiced)}.
            </span>{' '}
            {row.coverage.gapReportedElsewhere > 0 && (
              <>{fmtEURExact(row.coverage.gapReportedElsewhere)} of that falls in a month where the
                same brand does appear in their files under another account id, so the earnings have
                moved rather than gone. </>
            )}
            {row.coverage.gapAbsent > 0 && (
              <>{fmtEURExact(row.coverage.gapAbsent)} falls in a month their files do not describe at
                all. </>
            )}
            The account is scored at zero for these months either way, which pushes it toward looking
            overpaid.
          </p>
        )}
        {row.coverage.partialMonths > 0 && (
          <p className="text-[11px] text-zinc-500 leading-relaxed">
            A further {row.coverage.partialMonths} months are reported at under half of what was
            invoiced, a wedge of {fmtEURExact(row.coverage.partialWedge)}.
          </p>
        )}
        {row.coverage.monthLevelLift > 1 && (
          <p className="text-[11px] text-zinc-500 leading-relaxed">
            Choosing the best source month by month rather than once for the account would value it at
            {' '}{fmtEURExact(row.coverage.monthLevelEarned)} instead of {fmtEURExact(row.earnedTotal)},
            because the winning source is silent in months another source covers.
          </p>
        )}
      </div>
    )}

    {row.earnedTail > 0 && (
      <div className="text-[11px] text-zinc-500">
        Dec 2024 onward adds <span className="text-purple-400 tabular-nums">{fmtEURExact(row.earnedTail)}</span> from
        their own monthly columns
        {row.earnedTailFromAc > 0 && (
          <> (Affiliation Cloud shows <span className="tabular-nums">{fmtEURExact(row.earnedTailFromAc)}</span> for the same months)</>
        )}.
      </div>
    )}

    <MonthlyComparison row={row} />

    {row.invoiceEntries.length > 0 && (
      <div>
        <p className="text-[11px] text-zinc-500 mb-2">
          Adwin's invoice lines placed on this account
        </p>
        <div className="space-y-2">
          {row.invoiceEntries.map((entry, i) => (
            <div key={`${entry.label}|${i}`} className="border border-[hsl(225,15%,18%)] rounded-lg p-3">
              <div className="flex items-start justify-between gap-3 mb-2">
                <span className="text-[11px] text-amber-400/90 break-all">
                  {entry.label}
                  {entry.rules && entry.rules.length > 0 && (
                    <span
                      className="ml-2 text-[10px] text-zinc-500"
                      title="How these lines were placed on this account"
                    >
                      placed by {entry.rules.map((r) => RULE_LABELS[r] || r).join(', ')}
                      {entry.judged ? ` · ${entry.judged} line${entry.judged === 1 ? '' : 's'} on account size` : ''}
                      {entry.split ? ` · ${entry.split} line${entry.split === 1 ? '' : 's'} apportioned in Raketech's reported proportions` : ''}
                      {entry.contested && entry.contested.length > 0 ? ` · also matched ${entry.contested.join(', ')}` : ''}
                    </span>
                  )}
                </span>
                <span className="text-[11px] text-zinc-300 tabular-nums shrink-0">
                  {fmtEURExact(entry.revenue)}
                  {entry.ftds > 0 && (
                    <span className="text-zinc-600"> · {fmtInt(entry.ftds)} FTDs</span>
                  )}
                </span>
              </div>
              {entry.monthly.length > 0 && (
                <table className="w-full max-w-md text-[10px]">
                  <thead>
                    <tr className="text-zinc-600">
                      <th className="text-left py-0.5 pr-2 font-medium">Month</th>
                      <th className="text-right py-0.5 px-2 font-medium">FTDs</th>
                      <th className="text-right py-0.5 pl-2 font-medium">Invoiced</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-[hsl(225,15%,13%)]">
                    {entry.monthly.map((m) => (
                      <tr key={m.month} className="text-zinc-400">
                        <td className="py-0.5 pr-2">{fmtMonth(m.month)}</td>
                        <td className="py-0.5 px-2 text-right tabular-nums">{m.ftds ? fmtInt(m.ftds) : '—'}</td>
                        <td className="py-0.5 pl-2 text-right tabular-nums">{fmtEUR(m.revenue)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
          ))}
        </div>
      </div>
    )}

    {row.offers.length > 0 && (
      <div>
        <p className="text-[11px] text-zinc-500 mb-2">
          Offers Raketech lists for this account in the new report
        </p>
        <div className="flex flex-wrap gap-1.5">
          {row.offers.map((o) => (
            <span
              key={`${o.offer}|${o.brand}`}
              className="px-2 py-1 rounded border border-[hsl(225,15%,20%)] bg-[hsl(225,15%,11%)] text-[10px] text-zinc-400"
            >
              {o.brand || o.offer}
              {o.pa && <span className="text-amber-500/70"> · PA</span>}
              <span className="text-zinc-600"> {fmtEUR(o.totalEarnings)}</span>
            </span>
          ))}
        </div>
      </div>
    )}
  </div>
);

export default RaketechFinalReportV4;
